import { UIMetadataComplete } from "../../../types/UIMetadata";

/**
 * GMC Yukon (linkId 2368).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2368.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the GMC palette + `Stratum GMC` face verbatim
 * from 2385/2382/2377.
 *
 * DATA GAP: every 1.0 image reference for this project (colours,
 * features, accessories, packages, variant catalog) is stored without a
 * file extension and does not resolve on the deployed 1.0 host under any
 * extension tried — confirmed this is a genuine GCS 404 (via
 * x-goog-generation response headers), not a host/routing problem, and
 * that the 1.0 app itself has no extension-guessing logic either, so the
 * 1.0 PRODUCTION configurator is equally unable to render these renders.
 * Every `image` field below is left as an empty string rather than a
 * fabricated path. Presentation-level assets (video, slider,
 * transition logo, HDRI backgrounds) exist and are wired normally.
 *
 * Dropped: 1.0's `engine`/`transmission` filterOptions (generic
 * Petrol/Turbo Petrol, CVT/MT/AMT boilerplate found across every GMC project
 * so far, unrelated to any real trim distinction here). `toggleTabs` hides
 * CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2368/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Yukon: {
      variants: {
        "elevation-4wd": {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: "2026",
          configurationId: "mlfv94crqnmb",
          name: {
            en: "Elevation",
            ar: "Elevation",
          },
          description: {
            en: "4WD",
            ar: "الدفع الرباعي",
          },
          drivetrain: "4WD",
          trim: "Elevation",
          highlights: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "Torque 518/624 NM",
              ar: "عزم الدوران 624/518 نيوتن متر",
            },
            {
              en: "V8 Engine 5.3L/6.2L",
              ar: "V8 محرك 5.3 لتر/6.2 لتر",
            },
          ],
          specs: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "518/624 NM",
              ar: "624/518 نيوتن متر",
            },
          ],
          image: "",
          price: {
            KSA: 254450,
            UAE: 249700,
            OMR: 26463,
            BHD: 25784,
            QAR: 246986,
            JOD: 48176,
            KW: 21035,
            LBP: 67853,
            IQD: 67853,
            default: 249700,
          },
          basePrice: {
            UAE: 249700,
            KSA: 254996,
            KW: 20851,
            OMR: 25120,
            JOD: 77300,
            LBP: 88797,
            BHD: 25643,
            IQD: 89036731,
            QAR: 249571,
            default: 249700,
          },
          index: 0,
          isDefault: false,
          ar: {
            usdz: "",
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: "4SA",
          brandItemCode: "GCYU01",
        },
        "elevation-2wd": {
          leadModelYear: "2026",
          configurationId: "csozs5qjl9ri",
          name: {
            en: "Elevation",
            ar: "Elevation",
          },
          description: {
            en: "2WD",
            ar: "الدفع بعجلتين",
          },
          drivetrain: "2WD",
          trim: "Elevation",
          highlights: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "Torque 518/624 NM",
              ar: "عزم الدوران 624/518 نيوتن متر",
            },
            {
              en: "V8 Engine 5.3L/6.2L",
              ar: "V8 محرك 5.3 لتر/6.2 لتر",
            },
          ],
          specs: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "518/624 NM",
              ar: "624/518 نيوتن متر",
            },
          ],
          image: "",
          price: {
            KSA: 244260,
            UAE: 239700,
            OMR: 25403,
            BHD: 24752,
            QAR: 237095,
            JOD: 46246,
            KW: 20192,
            LBP: 65136,
            IQD: 65136,
            default: 239700,
          },
          basePrice: {
            UAE: 239700,
            KSA: 244779,
            KW: 20017,
            OMR: 25120,
            LBP: 88797,
            JOD: 77300,
            BHD: 24634,
            IQD: 85470983,
            QAR: 239097,
            default: 239700,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: "",
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: "4SA",
          brandItemCode: "GCYU02",
        },
        at4: {
          leadModelYear: "2026",
          configurationId: "zarlak3iq02p",
          name: {
            en: "AT4",
            ar: "AT4",
          },
          description: {
            en: "4WD",
            ar: "الدفع الرباعي",
          },
          drivetrain: "4WD",
          trim: "AT4",
          highlights: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "Torque 518/624 NM",
              ar: "عزم الدوران 624/518 نيوتن متر",
            },
            {
              en: "V8 Engine 5.3L/6.2L",
              ar: "V8 محرك 5.3 لتر/6.2 لتر",
            },
          ],
          specs: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "518/624 NM",
              ar: "624/518 نيوتن متر",
            },
          ],
          image: "",
          price: {
            KSA: 299389,
            UAE: 293800,
            OMR: 31136,
            BHD: 30338,
            QAR: 290607,
            JOD: 56684,
            KW: 24749,
            LBP: 79837,
            IQD: 79837,
            default: 293800,
          },
          basePrice: {
            UAE: 293800,
            KSA: 300026,
            KW: 24527,
            OMR: 30620,
            LBP: 104475,
            JOD: 90900,
            BHD: 30179,
            IQD: 104825724,
            QAR: 292044,
            default: 293800,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: "",
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: "4SB",
          brandItemCode: "GCYU03",
        },
        "at4-ultimate": {
          leadModelYear: "2026",
          configurationId: "eaz9b0w2gghd",
          name: {
            en: "AT4 Ultimate",
            ar: "AT4 Ultimate",
          },
          description: {
            en: "4WD",
            ar: "الدفع الرباعي",
          },
          drivetrain: "4WD",
          trim: "AT4 Ultimate",
          highlights: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "Torque 518/624 NM",
              ar: "عزم الدوران 624/518 نيوتن متر",
            },
            {
              en: "V8 Engine 5.3L/6.2L",
              ar: "V8 محرك 5.3 لتر/6.2 لتر",
            },
          ],
          specs: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "518/624 NM",
              ar: "624/518 نيوتن متر",
            },
          ],
          image: "",
          price: {
            KSA: 337500,
            UAE: 331200,
            OMR: 35100,
            BHD: 34200,
            QAR: 327600,
            JOD: 63900,
            KW: 27900,
            LBP: 90000,
            IQD: 90000,
            default: 331200,
          },
          basePrice: {
            UAE: 331200,
            KSA: 338215,
            KW: 27673,
            OMR: 34140,
            LBP: 119015,
            JOD: 103800,
            BHD: 34020,
            IQD: 118169774,
            QAR: 329220,
            default: 331200,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: "",
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: "4SG",
          brandItemCode: "GCYU04",
        },
        denali: {
          leadModelYear: "2026",
          configurationId: "u5qm0qtkgk60",
          name: {
            en: "Denali",
            ar: "دينالي",
          },
          description: {
            en: "4WD",
            ar: "الدفع الرباعي",
          },
          drivetrain: "4WD",
          trim: "Denali",
          highlights: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "Torque 518/624 NM",
              ar: "عزم الدوران 624/518 نيوتن متر",
            },
            {
              en: "V8 Engine 5.3L/6.2L",
              ar: "V8 محرك 5.3 لتر/6.2 لتر",
            },
          ],
          specs: [
            {
              en: "355/420 HP",
              ar: "420/355 حصاناً",
            },
            {
              en: "518/624 NM",
              ar: "624/518 نيوتن متر",
            },
          ],
          image: "",
          price: {
            KSA: 352785,
            UAE: 346200,
            OMR: 36690,
            BHD: 35749,
            QAR: 342437,
            JOD: 66794,
            KW: 29164,
            LBP: 94076,
            IQD: 94076,
            default: 346200,
          },
          basePrice: {
            UAE: 346200,
            KSA: 353542,
            KW: 28926,
            LBP: 124771,
            OMR: 35650,
            JOD: 108900,
            BHD: 35561,
            IQD: 123521667,
            QAR: 344131,
            default: 346200,
          },
          index: 4,
          isDefault: true,
          ar: {
            usdz: "",
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: "5SA",
          brandItemCode: "GCYU05",
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: "images/2368/HDRI/Night.png",
          title: {
            en: "City Landscape 1",
            ar: "إطلالة على المدينة 1",
          },
        },
        g0ZA968_RpUe: {
          image: "images/2368/HDRI/hdri_2.png",
          title: {
            en: "Sand Dunes",
            ar: "الكثبان الرملية",
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          "7eez7nplog7a": {
            label: {
              en: "Onyx Black",
              ar: "أسود",
            },
            image: "images/2368/Exteriors/elevation_Onyx_Black.png",
            usdz: "red_black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 0,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Onyx_Black.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBA",
            brandItemCode: "EX17",
          },
          ruym5z4zk7bp: {
            label: {
              en: "Sterling Metallic",
              ar: "فضي معدني",
            },
            image: "images/2368/Exteriors/elevation_Sterling_Metallic.png",
            usdz: "red",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 1,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Sterling_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXD",
            brandItemCode: "EX20",
          },
          dtjmzz3bsfrl: {
            label: {
              en: "Midnight Pine",
              ar: "صنوبري ليلي",
            },
            image: "images/2368/Exteriors/elevation_Midnight_Pine.png",
            usdz: "black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 2,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Midnight_Pine.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBD",
            brandItemCode: "EX18",
          },
          wibnckqtlqxe: {
            label: {
              en: "Titanium Rush Metallic",
              ar: "تيتانيوم راش معدني",
            },
            image: "images/2368/Exteriors/elevation_Titanium_Rush_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            index: 3,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Titanium_Rush_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "G6M",
            brandItemCode: "EX16",
          },
          "2h6rvjratd39": {
            label: {
              en: "Volcanic Red Tintcoat",
              ar: "أحمر بركاني بطبقتين",
            },
            image: "images/2368/Exteriors/elevation_Volcanic_Red_Tintcoat.png",
            usdz: "blue",
            price: {
              KSA: 2125,
              UAE: 2085,
              OMR: 221,
              BHD: 215,
              QAR: 2062,
              JOD: 402,
              KW: 176,
              IQD: 567,
              LBP: 567,
              default: 2085,
            },
            index: 5,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Volcanic_Red_Tintcoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "GNT",
            brandItemCode: "EX19",
          },
          ys6fc5ecm0gr: {
            label: {
              en: "Summit White",
              ar: "أبيض",
            },
            image: "images/2368/Exteriors/elevation_Summit_White.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 6,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Summit_White.png",
            hasQuantity: false,
            autoIntelRPOCode: "GAZ",
            brandItemCode: "EX01",
          },
          u7k1meo46rf2: {
            label: {
              en: "Onyx Black",
              ar: "أسود",
            },
            image: "images/2368/Exteriors/elevation_Onyx_Black.png",
            usdz: "red_black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 8,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Onyx_Black.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBA",
            brandItemCode: "EX17",
          },
          ab8wplqlfok3: {
            label: {
              en: "Sterling Metallic",
              ar: "فضي معدني",
            },
            image: "images/2368/Exteriors/elevation_Sterling_Metallic.png",
            usdz: "red",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 9,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Sterling_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXD",
            brandItemCode: "EX20",
          },
          bhv32vzt52gs: {
            label: {
              en: "Midnight Pine",
              ar: "صنوبري ليلي",
            },
            image: "images/2368/Exteriors/elevation_Midnight_Pine.png",
            usdz: "black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 10,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Midnight_Pine.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBD",
            brandItemCode: "EX18",
          },
          htk1s9thjb4s: {
            label: {
              en: "Titanium Rush Metallic",
              ar: "تيتانيوم راش معدني",
            },
            image: "images/2368/Exteriors/elevation_Titanium_Rush_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            index: 11,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Titanium_Rush_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "G6M",
            brandItemCode: "EX16",
          },
          "6p8m9s4ixtls": {
            label: {
              en: "Volcanic Red Tintcoat",
              ar: "أحمر بركاني بطبقتين",
            },
            image: "images/2368/Exteriors/elevation_Volcanic_Red_Tintcoat.png",
            usdz: "blue",
            price: {
              KSA: 2125,
              UAE: 2085,
              OMR: 221,
              BHD: 215,
              QAR: 2062,
              JOD: 402,
              KW: 176,
              IQD: 567,
              LBP: 567,
              default: 2085,
            },
            index: 13,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Volcanic_Red_Tintcoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "GNT",
            brandItemCode: "EX19",
          },
          i6xtclih8key: {
            label: {
              en: "Summit White",
              ar: "أبيض",
            },
            image: "images/2368/Exteriors/elevation_Summit_White.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 14,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/elevation_Summit_White.png",
            hasQuantity: false,
            autoIntelRPOCode: "GAZ",
            brandItemCode: "EX01",
          },
          oibbaumbmk6o: {
            label: {
              en: "Onyx Black",
              ar: "أسود",
            },
            image: "images/2368/Exteriors/at4_Onyx_Black.png",
            usdz: "red_black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 16,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Onyx_Black.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBA",
            brandItemCode: "EX17",
          },
          y6wvhj01w1qp: {
            label: {
              en: "Sterling Metallic",
              ar: "فضي معدني",
            },
            image: "images/2368/Exteriors/at4_Sterling_Metallic.png",
            usdz: "red",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 17,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Sterling_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXD",
            brandItemCode: "EX20",
          },
          omegypbnovxt: {
            label: {
              en: "Midnight Pine",
              ar: "صنوبري ليلي",
            },
            image: "images/2368/Exteriors/at4_Midnight_Pine.png",
            usdz: "black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 18,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Midnight_Pine.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBD",
            brandItemCode: "EX18",
          },
          lvou9g0gwg5m: {
            label: {
              en: "Titanium Rush Metallic",
              ar: "تيتانيوم راش معدني",
            },
            image: "images/2368/Exteriors/at4_Titanium_Rush_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            index: 19,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Titanium_Rush_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "G6M",
            brandItemCode: "EX16",
          },
          w73rxovz5ab4: {
            label: {
              en: "Volcanic Red Tintcoat",
              ar: "أحمر بركاني بطبقتين",
            },
            image: "images/2368/Exteriors/at4_Volcanic_Red_Tintcoat.png",
            usdz: "blue",
            price: {
              KSA: 2125,
              UAE: 2085,
              OMR: 221,
              BHD: 215,
              QAR: 2062,
              JOD: 402,
              KW: 176,
              IQD: 567,
              LBP: 567,
              default: 2085,
            },
            index: 21,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Volcanic_Red_Tintcoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "GNT",
            brandItemCode: "EX19",
          },
          yomo9egs18h1: {
            label: {
              en: "Summit White",
              ar: "أبيض",
            },
            image: "images/2368/Exteriors/at4_Summit_White.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 22,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4_Summit_White.png",
            hasQuantity: false,
            autoIntelRPOCode: "GAZ",
            brandItemCode: "EX01",
          },
          zxajfs4w3ai1: {
            label: {
              en: "Onyx Black",
              ar: "أسود",
            },
            image: "images/2368/Exteriors/at4ult_Onyx_Black.png",
            usdz: "red_black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 24,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Onyx_Black.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBA",
            brandItemCode: "EX17",
          },
          iwd9wmvjvi8i: {
            label: {
              en: "Sterling Metallic",
              ar: "فضي معدني",
            },
            image: "images/2368/Exteriors/at4ult_Sterling_Metallic.png",
            usdz: "red",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 25,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Sterling_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXD",
            brandItemCode: "EX20",
          },
          mlkmxquzqkwn: {
            label: {
              en: "Midnight Pine",
              ar: "صنوبري ليلي",
            },
            image: "images/2368/Exteriors/at4ult_Midnight_Pine.png",
            usdz: "black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 26,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Midnight_Pine.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBD",
            brandItemCode: "EX18",
          },
          "3q9yrk5nr6i1": {
            label: {
              en: "Titanium Rush Metallic",
              ar: "تيتانيوم راش معدني",
            },
            image: "images/2368/Exteriors/at4ult_Titanium_Rush_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            index: 27,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Titanium_Rush_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "G6M",
            brandItemCode: "EX16",
          },
          p80xu92utnwf: {
            label: {
              en: "Volcanic Red Tintcoat",
              ar: "أحمر بركاني بطبقتين",
            },
            image: "images/2368/Exteriors/at4ult_Volcanic_Red_Tintcoat.png",
            usdz: "blue",
            price: {
              KSA: 2125,
              UAE: 2085,
              OMR: 221,
              BHD: 215,
              QAR: 2062,
              JOD: 402,
              KW: 176,
              IQD: 567,
              LBP: 567,
              default: 2085,
            },
            index: 29,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Volcanic_Red_Tintcoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "GNT",
            brandItemCode: "EX19",
          },
          sz19i9v0nu0s: {
            label: {
              en: "Summit White",
              ar: "أبيض",
            },
            image: "images/2368/Exteriors/at4ult_Summit_White.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 30,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/at4ult_Summit_White.png",
            hasQuantity: false,
            autoIntelRPOCode: "GAZ",
            brandItemCode: "EX01",
          },
          "7du8wypy1esm": {
            label: {
              en: "Onyx Black",
              ar: "أسود",
            },
            image: "images/2368/Exteriors/denali_Onyx_Black.png",
            usdz: "red_black",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 32,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Onyx_Black.png",
            hasQuantity: false,
            autoIntelRPOCode: "GBA",
            brandItemCode: "EX17",
          },
          y3ynqbe53hl4: {
            label: {
              en: "Sterling Metallic",
              ar: "فضي معدني",
            },
            image: "images/2368/Exteriors/denali_Sterling_Metallic.png",
            usdz: "red",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 33,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Sterling_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXD",
            brandItemCode: "EX20",
          },
          cw20iz1f9qih: {
            label: {
              en: "Titanium Rush Metallic",
              ar: "تيتانيوم راش معدني",
            },
            image: "images/2368/Exteriors/denali_Titanium_Rush_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            index: 35,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Titanium_Rush_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "G6M",
            brandItemCode: "EX16",
          },
          ynov100f4usu: {
            label: {
              en: "Downpour Metallic",
              ar: "مطري معدني",
            },
            image: "images/2368/Exteriors/denali_Downpour_Metallic.png",
            usdz: "blue",
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            index: 36,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Downpour_Metallic.png",
            hasQuantity: false,
            autoIntelRPOCode: "GXP",
            brandItemCode: "EX21",
          },
          demq3ofi5wpc: {
            label: {
              en: "Volcanic Red Tintcoat",
              ar: "أحمر بركاني بطبقتين",
            },
            image: "images/2368/Exteriors/denali_Volcanic_Red_Tintcoat.png",
            usdz: "blue",
            price: {
              KSA: 2125,
              UAE: 2085,
              OMR: 221,
              BHD: 215,
              QAR: 2062,
              JOD: 402,
              KW: 176,
              IQD: 567,
              LBP: 567,
              default: 2085,
            },
            index: 37,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Volcanic_Red_Tintcoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "GNT",
            brandItemCode: "EX19",
          },
          i8d4rlmjov72: {
            label: {
              en: "Summit White",
              ar: "أبيض",
            },
            image: "images/2368/Exteriors/denali_Summit_White.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 38,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_Summit_White.png",
            hasQuantity: false,
            autoIntelRPOCode: "GAZ",
            brandItemCode: "EX01",
          },
          aozw0ed5yx7k: {
            label: {
              en: "White Frost Tricoat",
              ar: "أبيض ثلجي بثلاث طبقات",
            },
            image: "images/2368/Exteriors/denali_White_Frost_Tricoat.png",
            usdz: "blue",
            price: {
              KSA: 557,
              UAE: 547,
              OMR: 58,
              BHD: 56,
              QAR: 541,
              JOD: 106,
              KW: 46,
              IQD: 149,
              LBP: 149,
              default: 547,
            },
            index: 39,
            type: "exterior",
            cardType: "cart",
            cartImage: "images/2368/Exteriors/denali_White_Frost_Tricoat.png",
            hasQuantity: false,
            autoIntelRPOCode: "G1W",
            brandItemCode: "EX15",
          },
        },
        interior: {
          xyus3yrafri2: {
            label: {
              en: "Dark Atmosphere / Gideon",
              ar: "بيج داكن / جيديون",
            },
            image: "images/2368/Interiors/Gideon_Dark_Atmosphere.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/Gideon_Dark_Atmosphere.png",
            hasQuantity: false,
            autoIntelRPOCode: "HVE",
            brandItemCode: "IN05",
          },
          hp43ibg9l4ft: {
            label: {
              en: "Dark Atmosphere / Gideon",
              ar: "بيج داكن / جيديون",
            },
            image: "images/2368/Interiors/Gideon_Dark_Atmosphere.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/Gideon_Dark_Atmosphere.png",
            hasQuantity: false,
            autoIntelRPOCode: "HVE",
            brandItemCode: "IN05",
          },
          oz73ndw6lbhk: {
            label: {
              en: "Forest Storm with Mahogany Accents",
              ar: "زيتي داكن مع لمسات الماهوغني",
            },
            image: "images/2368/Interiors/Forest_Strom_Mahogany_Accents.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 2,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/Forest_Strom_Mahogany_Accents.png",
            hasQuantity: false,
            autoIntelRPOCode: "EA8",
            brandItemCode: "IN06",
          },
          "81dr50kw77gu": {
            label: {
              en: "Obsedian Rush With Red Accents",
              ar: "أوبسيديان راش مع لمسات باللون الأحمر",
            },
            image: "images/2368/Interiors/Obsidian_Red_Accents.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 3,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/Obsidian_Red_Accents.png",
            hasQuantity: false,
            autoIntelRPOCode: "EA5",
            brandItemCode: "IN66",
          },
          zk7xg8dg1ekn: {
            label: {
              en: "Jet Black",
              ar: "أسود داكن",
            },
            image: "images/2368/Interiors/JetBlack.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 4,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/JetBlack.png",
            hasQuantity: false,
            autoIntelRPOCode: "H2X",
            brandItemCode: "IN49",
          },
          bmbnnt678r0o: {
            label: {
              en: "Teak/ Light Shale",
              ar: "خشب الساج / صلصالي فاتح",
            },
            image: "images/2368/Interiors/Teak_Light_Shale.png",
            usdz: "interior_red",
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 5,
            type: "interior",
            cardType: "cart",
            cartImage: "images/2368/Interiors/Teak_Light_Shale.png",
            hasQuantity: false,
            autoIntelRPOCode: "HFM",
            brandItemCode: "IN81",
          },
        },
      },
      features: {
        av8ukajwzjsm: {
          label: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          image: "images/2368/Features/elevation_20_inch_Machined_and_Painted_Satin_Graphite_Wheels.jpg",
          title: {
            en: '20" Alloy Wheel',
            ar: "عجلة معدنية 20 إنش",
          },
          desc: {
            en: '20" x 9" (50.8 cm x 22.9 cm) machined and painted Satin Graphite',
            ar: "20 إنش × 9 إنش (50.8 سم × 22.9 سم) مشغولة آلياً ومطلية بطبقة نهائية من الجرافيت الساتان",
          },
          type: "exterior",
        },
        "56b99wbs39ki": {
          label: {
            en: "Body Colored Outer Rear View Mirror",
            ar: "مرايا الرؤية الخلفية الخارجية بلون الهيكل",
          },
          image: "images/2368/Features/Body_Colored_Outside_Mirror_Cap.jpg",
          title: {
            en: "Body Colored Outer Rear View Mirror",
            ar: "مرايا الرؤية الخلفية الخارجية بلون الهيكل",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        slfnh3hjd6s8: {
          label: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          image: "images/2368/Features/Walkin_Lighting.jpg",
          title: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          desc: {
            en: "LED Headlamps, and LED Fog Lamps includes enhanced animation and walk-up lighting",
            ar: "مصابيح أمامية LED ومصابيح ضباب LED تتضمن رسوماً متحركة محسنة وإضاءة للمشي",
          },
          type: "exterior",
        },
        e31dagcaee09: {
          label: {
            en: "Roof Carrier",
            ar: "حامل السقف",
          },
          image: "images/2368/Features/Roof.jpg",
          title: {
            en: "Chrome Roof Carrier",
            ar: "حامل سقف من الكروم",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        "3ofodapu2ddp": {
          label: {
            en: "Front Compartment",
            ar: "حجرة في اللوحة الأمامية",
          },
          image: "images/2368/Features/elevation_Front_Compartment.jpg",
          title: {
            en: "Refrigerated Front Compartment",
            ar: "حجرة التبريد الأمامية",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        muey3bbku37k: {
          label: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          image: "images/2368/Features/elevation_Wireless_Charger.jpg",
          title: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          desc: {
            en: "Wireless Phone Charging, for portable devices",
            ar: "شحن الهاتف لاسلكياً، للأجهزة المحمولة",
          },
          type: "interior",
        },
        z7m52iyopf8c: {
          label: {
            en: "Trizone Front and Rear Air condition",
            ar: "مكيف هواء ثلاثي المناطق أمامي وخلفي",
          },
          image: "images/2368/Features/elevation_Front_and_Rear_AC.jpg",
          title: {
            en: "Tri-Zone Automatic Climate Control",
            ar: "نظام التحكم التلقائي في المناخ ثلاثي المناطق",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        awla0dgc0vgs: {
          label: {
            en: "Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية",
          },
          image: "images/2368/Features/elevation_Irvm.jpg",
          title: {
            en: "Light Sensitive Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية الحساسة للضوء",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        h0dy3khszwvy: {
          label: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          image: "images/2368/Features/elevation_Seats.jpg",
          title: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        vsefclj6vozz: {
          label: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          image: "images/2368/Features/elevation_Speedometer.jpg",
          title: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          desc: {
            en: '11" diagonal with high contrast display',
            ar: "شاشة قطرية مقاس 11 إنش ذات تباين عالٍ",
          },
          type: "interior",
        },
        "7ncmf61zbg9j": {
          label: {
            en: "Audio System",
            ar: "نظام الصوت",
          },
          image: "images/2368/Features/elevation_InfoScreen.jpg",
          title: {
            en: '16.8" diagonal Premium GMC Infotainment System',
            ar: "نظام معلومات وترفيه ممتاز بشاشة قياس 16.8 إنش",
          },
          desc: {
            en: "16.8\" diagonal Premium GMC Infotainment System, with high contrast display and local backlight dimming, with Google built-in compatibility, including navigation capability, color touch-screen, multi- touch display, connected apps, personalized profiles for each driver's settings, and Natural Voice Recognition",
            ar: " نظام المعلومات والترفيه الممتاز من GMC بشاشة 16.8 إنش، مع تباين واضح وإضاءة خلفية محلية خافتة، توافق مع خدمات جوجل المدمجة، نظام للملاحة، شاشة ملونة تعمل باللمس، شاشة متعددة اللمس، تطبيقات متصلة، إعدادات مخصصة لكل سائق، وميزة التعرف على الصوت الطبيعي.",
          },
          type: "interior",
        },
        jh03x1qu9di0: {
          label: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          image: "images/2368/Features/elevation_Steering_Shifter.jpg",
          title: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          desc: {
            en: "Advanced Electronic Column Shifter",
            ar: "ناقل عمود إلكتروني متقدم",
          },
          type: "interior",
        },
        qesaxf0nj162: {
          label: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          image: "images/2368/Features/elevation_Liftgate.jpg",
          title: {
            en: "Powered and Height Programmable Rear Liftgate",
            ar: "باب خلفي كهربائي، قابل للبرمجة حسب الارتفاع",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        et1jiatqexbt: {
          label: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          image: "images/2368/Features/elevation_20_inch_Machined_and_Painted_Satin_Graphite_Wheels.jpg",
          title: {
            en: '20" Alloy Wheel',
            ar: "عجلة معدنية 20 إنش",
          },
          desc: {
            en: '20" x 9" (50.8 cm x 22.9 cm) machined and painted Satin Graphite',
            ar: "20 إنش × 9 إنش (50.8 سم × 22.9 سم) مشغولة آلياً ومطلية بطبقة نهائية من الجرافيت الساتان",
          },
          type: "exterior",
        },
        kh1xk6tsachv: {
          label: {
            en: "Body Colored Outer Rear View Mirror",
            ar: "مرايا الرؤية الخلفية الخارجية بلون الهيكل",
          },
          image: "images/2368/Features/Body_Colored_Outside_Mirror_Cap.jpg",
          title: {
            en: "Body Colored Outer Rear View Mirror",
            ar: "مرايا الرؤية الخلفية الخارجية بلون الهيكل",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        n9uci5v0q0no: {
          label: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          image: "images/2368/Features/Walkin_Lighting.jpg",
          title: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          desc: {
            en: "LED Headlamps, and LED Fog Lamps includes enhanced animation and walk-up lighting",
            ar: "مصابيح أمامية LED ومصابيح ضباب LED تتضمن رسوماً متحركة محسنة وإضاءة للمشي",
          },
          type: "exterior",
        },
        "6jn6rb1ajk00": {
          label: {
            en: "Roof Carrier",
            ar: "حامل السقف",
          },
          image: "images/2368/Features/Roof.jpg",
          title: {
            en: "Chrome Roof Carrier",
            ar: "حامل سقف من الكروم",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        fzpdk9bjmbo4: {
          label: {
            en: "Front Compartment",
            ar: "حجرة في اللوحة الأمامية",
          },
          image: "images/2368/Features/elevation_Front_Compartment.jpg",
          title: {
            en: "Refrigerated Front Compartment",
            ar: "حجرة التبريد الأمامية",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        "1m71ax5f4id6": {
          label: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          image: "images/2368/Features/elevation_Wireless_Charger.jpg",
          title: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          desc: {
            en: "Wireless Phone Charging, for portable devices",
            ar: "شحن الهاتف لاسلكياً، للأجهزة المحمولة",
          },
          type: "interior",
        },
        thexucnyzlqx: {
          label: {
            en: "Trizone Front and Rear Air condition",
            ar: "مكيف هواء ثلاثي المناطق أمامي وخلفي",
          },
          image: "images/2368/Features/elevation_Front_and_Rear_AC.jpg",
          title: {
            en: "Tri-Zone Automatic Climate Control",
            ar: "نظام التحكم التلقائي في المناخ ثلاثي المناطق",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        fz9wua33l4wm: {
          label: {
            en: "Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية",
          },
          image: "images/2368/Features/elevation_Irvm.jpg",
          title: {
            en: "Light Sensitive Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية الحساسة للضوء",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        a5fob6prph1z: {
          label: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          image: "images/2368/Features/elevation_Seats.jpg",
          title: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        pro1ugk226q0: {
          label: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          image: "images/2368/Features/elevation_Speedometer.jpg",
          title: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          desc: {
            en: '11" diagonal with high contrast display',
            ar: "شاشة قطرية مقاس 11 إنش ذات تباين عالٍ",
          },
          type: "interior",
        },
        sp46ano5o1g6: {
          label: {
            en: "Audio System",
            ar: "نظام الصوت",
          },
          image: "images/2368/Features/elevation_InfoScreen.jpg",
          title: {
            en: '16.8" diagonal Premium GMC Infotainment System',
            ar: "نظام معلومات وترفيه ممتاز بشاشة قياس 16.8 إنش",
          },
          desc: {
            en: "16.8\" diagonal Premium GMC Infotainment System, with high contrast display and local backlight dimming, with Google built-in compatibility, including navigation capability, color touch-screen, multi- touch display, connected apps, personalized profiles for each driver's settings, and Natural Voice Recognition",
            ar: " نظام المعلومات والترفيه الممتاز من GMC بشاشة 16.8 إنش، مع تباين واضح وإضاءة خلفية محلية خافتة، توافق مع خدمات جوجل المدمجة، نظام للملاحة، شاشة ملونة تعمل باللمس، شاشة متعددة اللمس، تطبيقات متصلة، إعدادات مخصصة لكل سائق، وميزة التعرف على الصوت الطبيعي.",
          },
          type: "interior",
        },
        po1vxwjvhsua: {
          label: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          image: "images/2368/Features/elevation_Steering_Shifter.jpg",
          title: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          desc: {
            en: "Advanced Electronic Column Shifter",
            ar: "ناقل عمود إلكتروني متقدم",
          },
          type: "interior",
        },
        "10giu7b6ijpj": {
          label: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          image: "images/2368/Features/elevation_Liftgate.jpg",
          title: {
            en: "Powered and Height Programmable Rear Liftgate",
            ar: "باب خلفي كهربائي، قابل للبرمجة حسب الارتفاع",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        eph3gxhsoo3x: {
          label: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          image: "images/2368/Features/Machined_Painted_Carbon_Flash_Metallic.jpg",
          title: {
            en: '20" AT4 Wheel Design',
            ar: "تصميم عجلات AT4 مقاس 20 إنش",
          },
          desc: {
            en: '20" x 9" (50.8 cm x 22.9 cm) machined and painted Carbon Flash Metallic',
            ar: "20 إنش × 9 إنش (50.8 سم × 22.9 سم) تم تصنيعها وتشطيبها بطبقة نهائية من الكربون المعدني اللامع",
          },
          type: "exterior",
        },
        "1ttspnz1uru7": {
          label: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          image: "images/2368/Features/Walkin_Lighting.jpg",
          title: {
            en: "Animated LED Front Lighting",
            ar: "إضاءة أمامية LED متحركة",
          },
          desc: {
            en: "LED Headlamps, and LED Fog Lamps includes enhanced animation and walk-up lighting",
            ar: "مصابيح أمامية LED ومصابيح ضباب LED تتضمن رسوماً متحركة محسنة وإضاءة للمشي",
          },
          type: "exterior",
        },
        kkq9t0p36pre: {
          label: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          image: "images/2368/Features/High_Approach_Angle_Bumper.jpg",
          title: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          desc: {
            en: "Front Fascia Designed for Improved Ground Clearance",
            ar: "تم تصميم الواجهة الأمامية لتحسين الارتفاع عن مستوى الأرض",
          },
          type: "exterior",
        },
        "1ljgwnhfg5yu": {
          label: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          image: "images/2368/Features/Side_Steps.jpg",
          title: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          desc: {
            en: "Power Retractable Side step with Perimeter Lighting",
            ar: "عتبة جانبية كهربائية قابلة للطي مع إنارة محيطة",
          },
          type: "exterior",
        },
        "7nnmna0acbhq": {
          label: {
            en: "Painted Roof Carrier",
            ar: "حامل سقف مطلي",
          },
          image: "images/2368/Features/Painted_Roof_Carrier.jpg",
          title: {
            en: "Painted Roof Carrier",
            ar: "حامل سقف مطلي",
          },
          desc: {
            en: "Black Roof Carrier",
            ar: "حامل سقف باللون الأسود",
          },
          type: "exterior",
        },
        dvhzgcit9gq8: {
          label: {
            en: "Dual Exhaust System",
            ar: "نظام عادم مزدوج",
          },
          image: "images/2368/Features/Dual_Exhaust.jpg",
          title: {
            en: "Dual Exhaust System",
            ar: "نظام عادم مزدوج",
          },
          desc: {
            en: "Dual Exhaust System with Rectangular Twin Polished Outlets",
            ar: "نظام عادم مزدوج مع منفذين مستطيلين مصقولين.",
          },
          type: "exterior",
        },
        gj3zxztzghx9: {
          label: {
            en: "Red Recovery Hooks",
            ar: "حلقات سحب حمراء",
          },
          image: "images/2368/Features/Hooks.jpg",
          title: {
            en: "Red Recovery Hooks",
            ar: "حلقات سحب حمراء",
          },
          desc: {
            en: "Red Horizontal Recovery Hooks",
            ar: "حلقات سحب أفقية حمراء",
          },
          type: "exterior",
        },
        "6c4xfhxqttcj": {
          label: {
            en: "Suspension",
            ar: "نظام التعليق",
          },
          image: "images/2368/Features/Lift_gate.jpg",
          title: {
            en: "Suspension",
            ar: "نظام التعليق",
          },
          desc: {
            en: "Four Corner Air Ride Suspension with Ride Height Control",
            ar: "نظام تعليق هوائي عند الزوايا الأربع مع التحكم في ارتفاع الركوب المركبة",
          },
          type: "exterior",
        },
        y5rmij6r47yd: {
          label: {
            en: "Second Row Seats",
            ar: "مقاعد الصف الثاني",
          },
          image: "images/2368/Features/Second_Row_Seats.jpg",
          title: {
            en: "Heated Second Row Seats",
            ar: "مقاعد الصف الثاني قابلة للتدفئة",
          },
          desc: {
            en: "AT4 Forest Storm Leather Interior",
            ar: "   مقصورة AT4 داخلية من الجلد باللون الزيتي الداكن",
          },
          type: "interior",
        },
        "9gc6nlc7mqbm": {
          label: {
            en: "Front Compartment",
            ar: "حجرة في اللوحة الأمامية",
          },
          image: "images/2368/Features/Front_Compartment.jpg",
          title: {
            en: "Refrigerated Front Compartment",
            ar: "حجرة التبريد الأمامية",
          },
          desc: {
            en: "Front Center Console with Built-In Refrigerated Storage",
            ar: "كنسولة مركزية أمامية مع وحدة تخزين مبردة مدمجة",
          },
          type: "interior",
        },
        "9lug6s0cjccr": {
          label: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          image: "images/2368/Features/Wireless_Charger.jpg",
          title: {
            en: "Front Wireless Charger",
            ar: "شاحن لاسلكي أمامي",
          },
          desc: {
            en: "Wireless Phone Charging, for portable devices",
            ar: "شحن الهاتف لاسلكياً، للأجهزة المحمولة",
          },
          type: "interior",
        },
        g8laj4777we9: {
          label: {
            en: "Trizone Front and Rear Air condition",
            ar: "مكيف هواء ثلاثي المناطق أمامي وخلفي",
          },
          image: "images/2368/Features/Front_and_Rear_AC.jpg",
          title: {
            en: "Tri-Zone Automatic Climate Control",
            ar: "نظام التحكم التلقائي في المناخ ثلاثي المناطق",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        w2coh1cfcwcm: {
          label: {
            en: "Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية",
          },
          image: "images/2368/Features/Irvm.jpg",
          title: {
            en: "Light Sensitive Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية الحساسة للضوء",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        k379fev4n1nf: {
          label: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          image: "images/2368/Features/at4Seats.jpg",
          title: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        fmph3y1o9d79: {
          label: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          image: "images/2368/Features/Speedometer.jpg",
          title: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          desc: {
            en: '11" diagonal with high contrast display',
            ar: "شاشة قطرية مقاس 11 إنش ذات تباين عالٍ",
          },
          type: "interior",
        },
        dn9egqplaktn: {
          label: {
            en: "Audio System",
            ar: "نظام الصوت",
          },
          image: "images/2368/Features/InfoScreen.jpg",
          title: {
            en: '16.8" diagonal Premium GMC Infotainment System',
            ar: "نظام معلومات وترفيه ممتاز بشاشة قياس 16.8 إنش",
          },
          desc: {
            en: "16.8\" diagonal Premium GMC Infotainment System, with high contrast display and local backlight dimming, with Google built-in compatibility, including navigation capability, color touch-screen, multi- touch display, connected apps, personalized profiles for each driver's settings, and Natural Voice Recognition",
            ar: " نظام المعلومات والترفيه الممتاز من GMC بشاشة 16.8 إنش، مع تباين واضح وإضاءة خلفية محلية خافتة، توافق مع خدمات جوجل المدمجة، نظام للملاحة، شاشة ملونة تعمل باللمس، شاشة متعددة اللمس، تطبيقات متصلة، إعدادات مخصصة لكل سائق، وميزة التعرف على الصوت الطبيعي.",
          },
          type: "interior",
        },
        v3g75ng0qbtv: {
          label: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          image: "images/2368/Features/Steering_Shifter.jpg",
          title: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          desc: {
            en: "Advanced Electronic Column Shifter",
            ar: "ناقل عمود إلكتروني متقدم",
          },
          type: "interior",
        },
        "4mpkdm2j8ori": {
          label: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          image: "images/2368/Features/Lift_gate.jpg",
          title: {
            en: "Hands-Free & Presence Detection Power Liftgate",
            ar: "باب خلفي كهربائي يعمل بدون استخدام اليدين مع خاصية اكتشاف الحضور",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        sa7o4yw7u7mu: {
          label: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          image: "images/2368/Features/at4_ult_20inchWheelDesign.jpg",
          title: {
            en: '20" AT4 Ultimate Specific Wheel Design',
            ar: "تصميم العجلات AT4 Ultimate مقاس 20 إنش",
          },
          desc: {
            en: '20" x 9" (50.8 cm x 22.9 cm) machined and painted Talos Bronze Metallic',
            ar: "20 إنش × 9 إنش (50.8 سم × 22.9 سم) مشغولة آليًا ومطلية بطبقة نهائية من البرونز المعدني من شركة Talos",
          },
          type: "exterior",
        },
        "1slkxz7syxvp": {
          label: {
            en: "Walkin Lighting",
            ar: "إضاءة ووكين",
          },
          image: "images/2368/Features/Walkin_Lighting.jpg",
          title: {
            en: "Animated LED Front Lighting",
            ar: "إضاءة أمامية LED متحركة",
          },
          desc: {
            en: "LED Headlamps, and LED Fog Lamps includes enhanced animation and walk-up lighting",
            ar: "مصابيح أمامية LED ومصابيح ضباب LED تتضمن رسوماً متحركة محسنة وإضاءة للمشي",
          },
          type: "exterior",
        },
        zk4z2of152hu: {
          label: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          image: "images/2368/Features/High_Approach_Angle_Bumper.jpg",
          title: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          desc: {
            en: "Front Fascia Designed for Improved Ground Clearance",
            ar: "تم تصميم الواجهة الأمامية لتحسين الارتفاع عن مستوى الأرض",
          },
          type: "exterior",
        },
        ok95ya1c9f2c: {
          label: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          image: "images/2368/Features/Side_Steps.jpg",
          title: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          desc: {
            en: "Power Retractable Side step with Perimeter Lighting",
            ar: "عتبة جانبية كهربائية قابلة للطي مع إنارة محيطة",
          },
          type: "exterior",
        },
        "3j9lbneqzehv": {
          label: {
            en: "Painted Roof Carrier",
            ar: "حامل سقف مطلي",
          },
          image: "images/2368/Features/at4ulti_Painted_SunRoof_Carrier.jpg",
          title: {
            en: "Painted Roof Carrier",
            ar: "حامل سقف مطلي",
          },
          desc: {
            en: "Black Roof Carrier",
            ar: "حامل سقف باللون الأسود",
          },
          type: "exterior",
        },
        hbtmqob9llo2: {
          label: {
            en: "Dual Exhaust System",
            ar: "نظام عادم مزدوج",
          },
          image: "images/2368/Features/Dual_Exhaust.jpg",
          title: {
            en: "Dual Exhaust System",
            ar: "نظام عادم مزدوج",
          },
          desc: {
            en: "Dual Exhaust System with Rectangular Twin Polished Outlets",
            ar: "نظام عادم مزدوج مع منفذين مستطيلين مصقولين.",
          },
          type: "exterior",
        },
        "7rhtvb1tiuos": {
          label: {
            en: "Red Recovery Hooks",
            ar: "حلقات سحب حمراء",
          },
          image: "images/2368/Features/Hooks.jpg",
          title: {
            en: "Red Recovery Hooks",
            ar: "حلقات سحب حمراء",
          },
          desc: {
            en: "Red Horizontal Recovery Hooks",
            ar: "حلقات سحب أفقية حمراء",
          },
          type: "exterior",
        },
        x3gmjbedv48k: {
          label: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          image: "images/2368/Features/at4ulti_Painted_SunRoof_Carrier.jpg",
          title: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        xkcj3q0s0hmx: {
          label: {
            en: "Suspension",
            ar: "نظام التعليق",
          },
          image: "images/2368/Features/at4ulti_Liftgate.jpg",
          title: {
            en: "Suspension",
            ar: "نظام التعليق",
          },
          desc: {
            en: "Four Corner Air Ride Suspension with Ride Height Control",
            ar: "نظام تعليق هوائي عند الزوايا الأربع مع التحكم في ارتفاع الركوب المركبة",
          },
          type: "exterior",
        },
        as606nc1f2st: {
          label: {
            en: "Interior",
            ar: "الداخلية",
          },
          image: "images/2368/Features/at4ulti_Interior.jpg",
          title: {
            en: "AT4 Ultimate Specific Osedian Rush Leather Interior",
            ar: "مقصورة داخلية من جلد أوبسيديان راش الخاص بطراز AT4 Ultimate",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        "9nml5px074gn": {
          label: {
            en: "Front Compartment",
            ar: "حجرة في اللوحة الأمامية",
          },
          image: "images/2368/Features/at4ulti_Front_Compartment.jpg",
          title: {
            en: "Refrigerated Front Compartment",
            ar: "حجرة التبريد الأمامية",
          },
          desc: {
            en: "Front Center Console with Built-In Refrigerated Storage",
            ar: "كنسولة مركزية أمامية مع وحدة تخزين مبردة مدمجة",
          },
          type: "interior",
        },
        bzo98svovyn4: {
          label: {
            en: "Trizone Front and Rear Air condition",
            ar: "مكيف هواء ثلاثي المناطق أمامي وخلفي",
          },
          image: "images/2368/Features/at4ulti_Front_and_Rear_AC.jpg",
          title: {
            en: "Tri-Zone Automatic Climate Control",
            ar: "نظام التحكم التلقائي في المناخ ثلاثي المناطق",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        b5wh08by8oys: {
          label: {
            en: "Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية",
          },
          image: "images/2368/Features/at4ulti_Irvm.jpg",
          title: {
            en: "Light Sensitive Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية الحساسة للضوء",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        nr6nsou5gim9: {
          label: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          image: "images/2368/Features/at4ulti_Front_Seats.jpg",
          title: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        ct2q24w7mow6: {
          label: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          image: "images/2368/Features/at4ulti_Speedometer.jpg",
          title: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          desc: {
            en: '11" diagonal with high contrast display',
            ar: "شاشة قطرية مقاس 11 إنش ذات تباين عالٍ",
          },
          type: "interior",
        },
        "3h4c5jdf9m04": {
          label: {
            en: "Audio System",
            ar: "نظام الصوت",
          },
          image: "images/2368/Features/at4ulti_InfoScreen.jpg",
          title: {
            en: '16.8" diagonal Premium GMC Infotainment System',
            ar: "نظام معلومات وترفيه ممتاز بشاشة قياس 16.8 إنش",
          },
          desc: {
            en: "16.8\" diagonal Premium GMC Infotainment System, with high contrast display and local backlight dimming, with Google built-in compatibility, including navigation capability, color touch-screen, multi- touch display, connected apps, personalized profiles for each driver's settings, and Natural Voice Recognition",
            ar: " نظام المعلومات والترفيه الممتاز من GMC بشاشة 16.8 إنش، مع تباين واضح وإضاءة خلفية محلية خافتة، توافق مع خدمات جوجل المدمجة، نظام للملاحة، شاشة ملونة تعمل باللمس، شاشة متعددة اللمس، تطبيقات متصلة، إعدادات مخصصة لكل سائق، وميزة التعرف على الصوت الطبيعي.",
          },
          type: "interior",
        },
        c9v1hui7o32s: {
          label: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          image: "images/2368/Features/at4ulti_Steering_Shifter.jpg",
          title: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          desc: {
            en: "Advanced Electronic Column Shifter",
            ar: "ناقل عمود إلكتروني متقدم",
          },
          type: "interior",
        },
        gu4r063wtclq: {
          label: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          image: "images/2368/Features/at4ulti_Liftgate.jpg",
          title: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        "6l8f63wj44te": {
          label: {
            en: "Bucket Seats",
            ar: "مقاعد فردية حاضنة",
          },
          image: "images/2368/Features/at4ulti_Front_Seats.jpg",
          title: {
            en: "1st Row Bucket Seats",
            ar: "مقاعد فردية حاضنة في الصف الأول",
          },
          desc: {
            en: "16-Way Power Front Leather Seats with Massage",
            ar: "مقاعد أمامية جلدية كهربائية بـ 16 وضعية مع تدليك",
          },
          type: "interior",
        },
        k97e1b902d5w: {
          label: {
            en: "Heads-up display",
            ar: "شاشة عرض علوية أمامية",
          },
          image: "images/2368/Features/at4ulti_HUD.jpg",
          title: {
            en: '15" Head-Up Dsplay',
            ar: "شاشة عرض علوية أمامية مقاس 15 إنش",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        l729qcq5vbuh: {
          label: {
            en: 'Rear HVAC 8" Control Screen',
            ar: "شاشة التحكم الخلفية لنظام التدفئة والتهوية وتكييف الهواء مقاس 8 إنش",
          },
          image: "images/2368/Features/at4ulti_Center_Control_Screen.jpg",
          title: {
            en: 'Rear HVAC 8" Control Screen',
            ar: "شاشة التحكم الخلفية لنظام التدفئة والتهوية وتكييف الهواء مقاس 8 إنش",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        "9coc6cre7oka": {
          label: {
            en: "Dual Entertainment Screens",
            ar: "شاشات ترفيه مزدوجة",
          },
          image: "images/2368/Features/at4ult_DualScreen.jpg",
          title: {
            en: '12.6" Rear Seat Dual Entertainment Screens',
            ar: "شاشات ترفيهية مزدوجة للمقاعد الخلفية مقاس 12.6 انش",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        ylam62d7djz9: {
          label: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          image: "images/2368/Features/denali_Premium_Chrome_20_inch_Alloy_Wheels.jpg",
          title: {
            en: 'Premium Chrome 20" Alloy Wheels',
            ar: "عجلات معدنية ممتازة من الكروم مقاس 20 إنش",
          },
          desc: {
            en: '20" x 9" (50.8cm x 22.9cm) 6-Spoke Multi-Dimensional Polished Aluminum',
            ar: "20 إنش × 9 إنش (50.8 سم × 22.9 سم) من الألومنيوم المصقول متعدد الأبعاد بستة قضبان",
          },
          type: "exterior",
        },
        srix4ia0v5eq: {
          label: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          image: "images/2368/Features/Side_Steps.jpg",
          title: {
            en: "Black Power Retractable Side Step",
            ar: "عتبة جانبية كهربائية قابلة للطي باللون الأسود",
          },
          desc: {
            en: "Power Retractable Side step with Perimeter Lighting",
            ar: "عتبة جانبية كهربائية قابلة للطي مع إنارة محيطة",
          },
          type: "exterior",
        },
        yhpclqb0djib: {
          label: {
            en: "Chrome Roof Carrier",
            ar: "حامل سقف من الكروم",
          },
          image: "images/2368/Features/denali_Chrome_Roof_Carrier.jpg",
          title: {
            en: "Chrome Roof Carrier",
            ar: "حامل سقف من الكروم",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        gu8nnyhcoq3y: {
          label: {
            en: "Standard Low Bumper",
            ar: "مصد منخفض قياسي",
          },
          image: "images/2368/Features/denali_Standard_Low_Bumper.jpg",
          title: {
            en: "Standard Low Bumper",
            ar: "مصد منخفض قياسي",
          },
          desc: {
            en: "Denali Execlusive Chrome Grille and Animated LED Lighting",
            ar: "شبك أمامي من الكروم خاص بطراز دينالي وإضاءة LED متحركة",
          },
          type: "exterior",
        },
        "5djc26kkpfie": {
          label: {
            en: "Chrome Bodyside Molding",
            ar: "شرائح جانبية من الكروم للهيكل",
          },
          image: "images/2368/Features/denaliChrome_Body_Molding.jpg",
          title: {
            en: "Chrome Bodyside Molding",
            ar: "شرائح جانبية من الكروم للهيكل",
          },
          desc: {
            en: "Chrome Bodyside Molding & Chrome Door Handles",
            ar: "شرائح جانبية من الكروم للهيكل ومقابض من الكروم للأبواب",
          },
          type: "exterior",
        },
        kbns5f4ez323: {
          label: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          image: "images/2368/Features/denali_Power_Panoramic_Sunroof.jpg",
          title: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        rhjetiqsprrh: {
          label: {
            en: "Speaker",
            ar: "مكبرات الصوت",
          },
          image: "images/2368/Features/denali_Bose_Speaker.jpg",
          title: {
            en: "Bose 14 Speaker Surround with Centrepoint",
            ar: "نظام Bose الصوتي بـ 14 مكبر صوت مع Centrepoint",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        y6zcqp9r8fln: {
          label: {
            en: "Front Compartment",
            ar: "حجرة في اللوحة الأمامية",
          },
          image: "images/2368/Features/denaliFront_Compartment.jpg",
          title: {
            en: "Refrigerated Front Compartment",
            ar: "حجرة التبريد الأمامية",
          },
          desc: {
            en: "Front Center Console with Built-In Refrigerated Storage",
            ar: "كنسولة مركزية أمامية مع وحدة تخزين مبردة مدمجة",
          },
          type: "interior",
        },
        ddlwz0n6pzn8: {
          label: {
            en: "Trizone Front and Rear Air condition",
            ar: "مكيف هواء ثلاثي المناطق أمامي وخلفي",
          },
          image: "images/2368/Features/denali_Front_and_Rear_AC.jpg",
          title: {
            en: "Tri-Zone Automatic Climate Control",
            ar: "نظام التحكم التلقائي في المناخ ثلاثي المناطق",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        "9cffzj69hili": {
          label: {
            en: "Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية",
          },
          image: "images/2368/Features/denali_Irvm.jpg",
          title: {
            en: "Light Sensitive Inside Rear View Mirror",
            ar: "مرآة الرؤية الخلفية الداخلية الحساسة للضوء",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        e6jhw2a3jpjj: {
          label: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          image: "images/2368/Features/denali_Seats.jpg",
          title: {
            en: "Perforated Leather seats",
            ar: "مقاعد جلدية مثقبة",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
        hnbca1qrws9d: {
          label: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          image: "images/2368/Features/denali_Speedometer.jpg",
          title: {
            en: "Driver Information Center",
            ar: "مركز معلومات السائق",
          },
          desc: {
            en: '11" diagonal with high contrast display',
            ar: "شاشة قطرية مقاس 11 إنش ذات تباين عالٍ",
          },
          type: "interior",
        },
        b2e9rf4uwm3o: {
          label: {
            en: "Audio System",
            ar: "نظام الصوت",
          },
          image: "images/2368/Features/denali_Info_Screen.jpg",
          title: {
            en: '16.8" diagonal Premium GMC Infotainment System',
            ar: "نظام معلومات وترفيه ممتاز بشاشة قياس 16.8 إنش",
          },
          desc: {
            en: "16.8\" diagonal Premium GMC Infotainment System, with high contrast display and local backlight dimming, with Google built-in compatibility, including navigation capability, color touch-screen, multi- touch display, connected apps, personalized profiles for each driver's settings, and Natural Voice Recognition",
            ar: "نظام المعلومات والترفيه الممتاز من GMC بشاشة 16.8 إنش، مع تباين واضح وإضاءة خلفية محلية خافتة، توافق مع خدمات جوجل المدمجة، نظام للملاحة، شاشة ملونة تعمل باللمس، شاشة متعددة اللمس، تطبيقات متصلة، إعدادات مخصصة لكل سائق، وميزة التعرف على الصوت الطبيعي.",
          },
          type: "interior",
        },
        vox21clwc8i8: {
          label: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          image: "images/2368/Features/denali_Steering_Shifter.jpg",
          title: {
            en: "Steering Column Electronic shifter",
            ar: "ناقل حركة إلكتروني على عمود التوجيه",
          },
          desc: {
            en: "Advanced Electronic Column Shifter",
            ar: "ناقل عمود إلكتروني متقدم",
          },
          type: "interior",
        },
        jtptkqg4c1v0: {
          label: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          image: "images/2368/Features/denali_Liftgate.jpg",
          title: {
            en: "Liftgate",
            ar: "باب خلفي",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "exterior",
        },
        fmo7hw3rxeyz: {
          label: {
            en: "Bucket Seats",
            ar: "مقاعد فردية حاضنة",
          },
          image: "images/2368/Features/denali_Front_Seats.jpg",
          title: {
            en: "1st Row Bucket Seats",
            ar: "مقاعد فردية حاضنة في الصف الأول",
          },
          desc: {
            en: "16-Way Power Front Leather Seats with Massage",
            ar: "مقاعد أمامية جلدية كهربائية بـ 16 وضعية مع تدليك",
          },
          type: "interior",
        },
        "19t5w7j2faxq": {
          label: {
            en: "Dual Entertainment Screens",
            ar: "شاشات ترفيه مزدوجة",
          },
          image: "images/2368/Features/denali_Rear_Screen.jpg",
          title: {
            en: '12.6" Rear Seat Dual Entertainment Screens',
            ar: "شاشات ترفيهية مزدوجة للمقاعد الخلفية مقاس 12.6 انش",
          },
          desc: {
            en: "",
            ar: "",
          },
          type: "interior",
        },
      },
      accessories: {
        "3w06jsi5oinw": {
          label: {
            en: '20" Machined and Painted Satin Graphite',
            ar: "جرافيت ساتان مطلي ومُخرط مقاس 20 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R4J",
          brandItemCode: "OP36",
        },
        whv4p5vjna3p: {
          label: {
            en: '20" Polished Aluminium',
            ar: "ألومنيوم مصقول مقاس 20 إنش",
          },
          image: "images/2368/Accessories/Polished_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          cardType: "cart",
          autoIntelRPOCode: "RTL",
          brandItemCode: "AC131",
        },
        o0effisb7huz: {
          label: {
            en: '22" Carbon Flash Metallic Aluminium',
            ar: "22 إنش من الألومنيوم الكربوني المعدني",
          },
          image: "images/2368/Accessories/Carbon_Flash_Metallic_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 10705,
            UAE: 10505,
            OMR: 1113,
            BHD: 1085,
            QAR: 10391,
            JOD: 2027,
            KW: 885,
            IQD: 2855,
            LBP: 2855,
            default: 10505,
          },
          cardType: "cart",
          autoIntelRPOCode: "R4L",
          brandItemCode: "AC134",
        },
        arv6xc8di0p2: {
          label: {
            en: '22" Black Wheels',
            ar: "عجلات سوداء مقاس 22 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        z60cgrm95zsc: {
          label: {
            en: "Roof",
            ar: "السقف",
          },
          image: "",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte4: {
          label: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          image: "images/2368/Accessories/Optional_Sunroof_Elevation.jpg",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3U",
          brandItemCode: "AC201",
        },
        xw46c5hhztat: {
          label: {
            en: "WO_HUD_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 2635,
            UAE: 2586,
            OMR: 274,
            BHD: 267,
            QAR: 2558,
            JOD: 499,
            KW: 218,
            IQD: 703,
            LBP: 703,
            default: 2586,
          },
          cardType: "cart",
        },
        "7ua1p3j98kiy": {
          label: {
            en: "Heads-Up Display",
            ar: "شاشة عرض علوية أمامية",
          },
          image: "images/2368/Accessories/Optional_W_HUD_Elevation.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 2635,
            UAE: 2586,
            OMR: 274,
            BHD: 267,
            QAR: 2558,
            JOD: 499,
            KW: 218,
            IQD: 703,
            LBP: 703,
            default: 2586,
          },
          cardType: "cart",
          autoIntelRPOCode: "UV6",
          brandItemCode: "OP40",
        },
        t09zdke9k241: {
          label: {
            en: "Default Cargo Area Sill Plate",
            ar: "عتبة صندوق الأمتعة",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        x8m9byz4k9y4: {
          label: {
            en: "Cargo Area Sill Plate",
            ar: "عتبة حيز الأمتعة",
          },
          image: "images/2368/Accessories/CargoSillPlate.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3Q",
          excludeFromRecommendation: true,
          brandItemCode: "AC13",
        },
        f85e14irfgjr: {
          label: {
            en: "Emblems Black Illuminated Front Grille",
            ar: "شبك أمامي أسود مع شعارات مضيئة",
          },
          image: "images/2368/Accessories/elevation_Emblems_Black_Illuminated_Front_Grille.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R88",
          brandItemCode: "AC171",
        },
        ax1vrv62y67b: {
          label: {
            en: "LED Puddle Lights",
            ar: "مصابيح LED للإضاءة السفلية عند الأبواب",
          },
          image: "images/2368/Accessories/LED_Puddle_Lights.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "DXR",
          excludeFromRecommendation: true,
          brandItemCode: "AC195",
        },
        "38e3im5n9udc": {
          label: {
            en: "Floor Mat-Front",
            ar: "سجادة أرضية أمامية",
          },
          image: "images/2368/Accessories/elevation_Floor_Mat_Front.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "B58",
          excludeFromRecommendation: true,
          brandItemCode: "AC180",
        },
        "79bhii9ntvo9": {
          label: {
            en: "Black GMC Grille Emblems",
            ar: "شعارات شبك GMC باللون الأسود",
          },
          image: "images/2368/Accessories/elevation_Black_GMC_Grille_Emblems.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "SFZ",
          brandItemCode: "AC154",
        },
        "0cbl602swwhi": {
          label: {
            en: "Black Nameplates",
            ar: "شارات باللون الأسود",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "RIK",
          brandItemCode: "AC40",
        },
        s9zcho1l9gqv: {
          label: {
            en: "Illuminated Sill Plates",
            ar: "عتبات مضاءة",
          },
          image: "images/2368/Accessories/ACC_Illuminated_Sill_Yukon.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "S0M",
          excludeFromRecommendation: true,
          brandItemCode: "AC23",
        },
        fsedl2eprmy0: {
          label: {
            en: "12.6 Rear Seat Dual Entertainment Screens",
            ar: "شاشات ترفيه مزدوجة للمقاعد الخلفية 12.6 إنش",
          },
          image: "images/2368/Accessories/Optional_Screen_Elevation.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 6571,
            UAE: 6448,
            OMR: 683,
            BHD: 666,
            QAR: 6378,
            JOD: 1244,
            KW: 543,
            IQD: 1752,
            LBP: 1752,
            default: 6448,
          },
          cardType: "cart",
          autoIntelRPOCode: "UW9",
          brandItemCode: "OP34",
        },
        qqqb72272l8x: {
          label: {
            en: "WO_Technology_Package_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "68es97qgriyh": {
          label: {
            en: "W_Technology_Package_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        nuigmle8uxkr: {
          label: {
            en: "Max Trailering Package",
            ar: "فئة الحد الأقصى للقطر",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "NHT",
        },
        ol945v3g96o3: {
          label: {
            en: "variant_parts",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "6mdkhlqtw9qo": {
          label: {
            en: "5280 sku",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        bdhjvv0tik8h: {
          label: {
            en: "5280 sku",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte5: {
          label: {
            en: "Grill",
            ar: "الشبك الأمامي",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte6: {
          label: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte7: {
          label: {
            en: "Standard Low Bumper",
            ar: "مصد منخفض قياسي",
          },
          image: "images/2368/Accessories/Low_Bumper.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "VB5",
          excludeFromRecommendation: true,
          brandItemCode: "AC218",
        },
        o6izcb1ru5o0: {
          label: {
            en: '20" Machined and Painted Satin Graphite',
            ar: "جرافيت ساتان مطلي ومُخرط مقاس 20 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R4J",
          brandItemCode: "OP36",
        },
        "7dx04b353hiy": {
          label: {
            en: '20" Polished Aluminium',
            ar: "ألومنيوم مصقول مقاس 20 إنش",
          },
          image: "images/2368/Accessories/Polished_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          cardType: "cart",
          autoIntelRPOCode: "RTL",
          brandItemCode: "AC131",
        },
        iki6mhzk69f8: {
          label: {
            en: '22" Carbon Flash Metallic Aluminium',
            ar: "22 إنش من الألومنيوم الكربوني المعدني",
          },
          image: "images/2368/Accessories/Carbon_Flash_Metallic_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 10705,
            UAE: 10505,
            OMR: 1113,
            BHD: 1085,
            QAR: 10391,
            JOD: 2027,
            KW: 885,
            IQD: 2855,
            LBP: 2855,
            default: 10505,
          },
          cardType: "cart",
          autoIntelRPOCode: "R4L",
          brandItemCode: "AC134",
        },
        s507n4vlfqa8: {
          label: {
            en: '22" Black Wheels',
            ar: "عجلات سوداء مقاس 22 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "4f9y7baneis7": {
          label: {
            en: "Roof",
            ar: "السقف",
          },
          image: "",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "3sfzzmyqryib": {
          label: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          image: "images/2368/Accessories/Optional_Sunroof_Elevation.jpg",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3U",
          brandItemCode: "AC201",
        },
        "26hhkehlyy0f": {
          label: {
            en: "WO_HUD_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 2635,
            UAE: 2586,
            OMR: 274,
            BHD: 267,
            QAR: 2558,
            JOD: 499,
            KW: 218,
            IQD: 703,
            LBP: 703,
            default: 2586,
          },
          cardType: "cart",
        },
        i44vvp9s1vpl: {
          label: {
            en: "Heads-Up Display",
            ar: "شاشة عرض علوية أمامية",
          },
          image: "images/2368/Accessories/Optional_W_HUD_Elevation.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 2635,
            UAE: 2586,
            OMR: 274,
            BHD: 267,
            QAR: 2558,
            JOD: 499,
            KW: 218,
            IQD: 703,
            LBP: 703,
            default: 2586,
          },
          cardType: "cart",
          autoIntelRPOCode: "UV6",
          brandItemCode: "OP40",
        },
        "81u5znzgazct": {
          label: {
            en: "Default Cargo Area Sill Plate",
            ar: "عتبة صندوق الأمتعة",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "71dw4a7mytec": {
          label: {
            en: "Cargo Area Sill Plate",
            ar: "عتبة حيز الأمتعة",
          },
          image: "images/2368/Accessories/CargoSillPlate.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3Q",
          excludeFromRecommendation: true,
          brandItemCode: "AC13",
        },
        w75uxl15tnqs: {
          label: {
            en: "Emblems Black Illuminated Front Grille",
            ar: "شبك أمامي أسود مع شعارات مضيئة",
          },
          image: "images/2368/Accessories/elevation_Emblems_Black_Illuminated_Front_Grille.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R88",
          brandItemCode: "AC171",
        },
        p0l17y9nyau4: {
          label: {
            en: "LED Puddle Lights",
            ar: "مصابيح LED للإضاءة السفلية عند الأبواب",
          },
          image: "images/2368/Accessories/LED_Puddle_Lights.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "DXR",
          excludeFromRecommendation: true,
          brandItemCode: "AC195",
        },
        "8fmud7bac7m6": {
          label: {
            en: "Floor Mat-Front",
            ar: "سجادة أرضية أمامية",
          },
          image: "images/2368/Accessories/elevation_Floor_Mat_Front.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "B58",
          excludeFromRecommendation: true,
          brandItemCode: "AC180",
        },
        "6tz8c3ggabkt": {
          label: {
            en: "Black GMC Grille Emblems",
            ar: "شعارات شبك GMC باللون الأسود",
          },
          image: "images/2368/Accessories/elevation_Black_GMC_Grille_Emblems.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "SFZ",
          brandItemCode: "AC154",
        },
        x96rsh8arus4: {
          label: {
            en: "Black Nameplates",
            ar: "شارات باللون الأسود",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "RIK",
          brandItemCode: "AC40",
        },
        dwto2y3t0e5a: {
          label: {
            en: "Illuminated Sill Plates",
            ar: "عتبات مضاءة",
          },
          image: "images/2368/Accessories/ACC_Illuminated_Sill_Yukon.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "S0M",
          excludeFromRecommendation: true,
          brandItemCode: "AC23",
        },
        "0evjcvrb9ug7": {
          label: {
            en: "12.6 Rear Seat Dual Entertainment Screens",
            ar: "شاشات ترفيه مزدوجة للمقاعد الخلفية 12.6 إنش",
          },
          image: "images/2368/Accessories/Optional_Screen_Elevation.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 6571,
            UAE: 6448,
            OMR: 683,
            BHD: 666,
            QAR: 6378,
            JOD: 1244,
            KW: 543,
            IQD: 1752,
            LBP: 1752,
            default: 6448,
          },
          cardType: "cart",
          autoIntelRPOCode: "UW9",
          brandItemCode: "OP34",
        },
        d3hm2vqzttar: {
          label: {
            en: "WO_Technology_Package_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        jackzeoq4kxc: {
          label: {
            en: "W_Technology_Package_Elevation",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        vdumj7fgdp8a: {
          label: {
            en: "Max Trailering Package",
            ar: "فئة الحد الأقصى للقطر",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "NHT",
        },
        xomnqrfmrq7b: {
          label: {
            en: "variant_parts",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        ol945v3g96o323: {
          label: {
            en: "5280 sku",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        ol945v3g96o3435: {
          label: {
            en: "5280 sku",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte8: {
          label: {
            en: "Grill",
            ar: "الشبك الأمامي",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte9: {
          label: {
            en: "High Approach Angle Bumper",
            ar: "مصد بزاوية اقتراب عالية",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
        },
        k4u455dsgte10: {
          label: {
            en: "Standard Low Bumper",
            ar: "مصد منخفض قياسي",
          },
          image: "images/2368/Accessories/Low_Bumper.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "VB5",
          excludeFromRecommendation: true,
          brandItemCode: "AC218",
        },
        "21nuzdavocyl": {
          label: {
            en: '20" Machined and Painted Carbon Flash Metallic(Standard)',
            ar: "",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R4L",
          brandItemCode: "OP35",
        },
        kx1uza6dq5ot: {
          label: {
            en: '22" Black Wheels',
            ar: "عجلات سوداء مقاس 22 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "4efdsd97ofsv": {
          label: {
            en: "Yukon_Default_sill_plate_Cargo",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        hmx6xdr280yk: {
          label: {
            en: "Cargo Area Sill Plate",
            ar: "عتبة حيز الأمتعة",
          },
          image: "images/2368/Accessories/CargoSillPlate.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3Q",
          excludeFromRecommendation: true,
          brandItemCode: "AC13",
        },
        lzb3mhbvi684: {
          label: {
            en: "Emblems Black Illuminated Front Grille",
            ar: "شبك أمامي أسود مع شعارات مضيئة",
          },
          image: "images/2368/Accessories/at4_Emblems_Black_Illuminated_Front_Grille.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R88",
          brandItemCode: "AC171",
        },
        "7uw2r7k9jvjc": {
          label: {
            en: "LED Puddle Lights",
            ar: "مصابيح LED للإضاءة السفلية عند الأبواب",
          },
          image: "images/2368/Accessories/LED_Puddle_Lights.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "DXR",
          excludeFromRecommendation: true,
          brandItemCode: "AC195",
        },
        ibzcobiml3cq: {
          label: {
            en: "Floor Mat-Front",
            ar: "سجادة أرضية أمامية",
          },
          image: "images/2368/Accessories/at4_Floor_Mat_Front.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 599,
            UAE: 588,
            OMR: 62,
            BHD: 61,
            QAR: 582,
            JOD: 113,
            KW: 50,
            IQD: 160,
            LBP: 160,
            default: 588,
          },
          cardType: "cart",
          autoIntelRPOCode: "B58",
          excludeFromRecommendation: true,
          brandItemCode: "AC180",
        },
        rv7bg4mdrw95: {
          label: {
            en: "Black GMC Grille Emblems",
            ar: "شعارات شبك GMC باللون الأسود",
          },
          image: "images/2368/Accessories/at4_Black_GMC_Grille_Emblems.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "SFZ",
          brandItemCode: "AC154",
        },
        "49blm589zyjf": {
          label: {
            en: "Black Nameplates AT4",
            ar: "شارات سوداء AT4",
          },
          image: "images/2368/Accessories/at4_Black_Nameplates_AT4.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "RIK",
          brandItemCode: "AC155",
        },
        b32ce7powkrp: {
          label: {
            en: "Illuminated Sill Plates",
            ar: "عتبات مضاءة",
          },
          image: "images/2368/Accessories/ACC_Illuminated_Sill_Yukon.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "S0M",
          excludeFromRecommendation: true,
          brandItemCode: "AC23",
        },
        "71l0ft0t7zv8": {
          label: {
            en: "Black Exhaust Tip",
            ar: "طرف عادم أسود",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "VTA",
          brandItemCode: "AC24",
        },
        "529r8tz9f40j": {
          label: {
            en: "Mid_Bench_Seat_AT4",
            ar: "",
          },
          image: "images/2368/Accessories/Default_Mid_Bench_Seat_AT4.jpg",
          category: {
            en: "Seats",
            ar: "المقاعد",
          },
          price: {
            KSA: 2619,
            UAE: 2569.63,
            OMR: 272,
            BHD: 265,
            QAR: 2542,
            JOD: 496,
            KW: 216,
            LBP: 698,
            IQD: 698,
            default: 2569.63,
          },
          cardType: "cart",
        },
        rdzllyd6co8k: {
          label: {
            en: "Second Row Bucket Power config Seats",
            ar: "مقاعد فردية كهربائية قابلة للتعديل في الصف الثاني",
          },
          image: "images/2368/Accessories/Default_Mid_Bench_Seat_AT4.jpg",
          category: {
            en: "Seats",
            ar: "المقاعد",
          },
          price: {
            KSA: 2619,
            UAE: 2570,
            OMR: 272,
            BHD: 265,
            QAR: 2542,
            JOD: 496,
            KW: 216,
            IQD: 698,
            LBP: 698,
            default: 2570,
          },
          cardType: "cart",
          autoIntelRPOCode: "A50",
          brandItemCode: "AC210",
        },
        cve371it9s7h: {
          label: {
            en: "Roof",
            ar: "السقف",
          },
          image: "",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "3u6vprohtrk6": {
          label: {
            en: "Power Panaromic Sunroof",
            ar: "فتحة سقف بانورامية كهربائية",
          },
          image: "images/2368/Accessories/Optional_Sunroof_AT4.jpg",
          category: {
            en: "Roof",
            ar: "السقف",
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3U",
          brandItemCode: "AC201",
        },
        w61wki046nr7: {
          label: {
            en: "12.6 Rear Seat Dual Entertainment Screens",
            ar: "شاشات ترفيه مزدوجة للمقاعد الخلفية 12.6 إنش",
          },
          image: "images/2368/Accessories/Optional_Screen_AT4.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 6571,
            UAE: 6448,
            OMR: 683,
            BHD: 666,
            QAR: 6378,
            JOD: 1244,
            KW: 543,
            IQD: 1752,
            LBP: 1752,
            default: 6448,
          },
          cardType: "cart",
          autoIntelRPOCode: "UW9",
          brandItemCode: "OP34",
        },
        ifgq6v1uhb9h: {
          label: {
            en: "WO_Technology_Package_AT4",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "7vmqxynu27wn": {
          label: {
            en: "W_Technology_Package_AT4",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        aii5h9cgyg14: {
          label: {
            en: "WO_Advanced_Security_Pack_AT4",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        z50pkzqtlb0j: {
          label: {
            en: "W_Advanced_Security_Pack_AT4",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        d1se9jwnr8q9: {
          label: {
            en: "variant_parts",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "788dseof5kp6": {
          label: {
            en: "6.2L EcoTec3 V8 engine",
            ar: "محرك EcoTec3 V8 سعة 6.2 لتر",
          },
          image: "images/2368/Accessories/Engine62L.avif",
          category: {
            en: "Performance",
            ar: "الأداء",
          },
          price: {
            KSA: 8217,
            UAE: 8064,
            OMR: 855,
            BHD: 833,
            QAR: 7976,
            JOD: 1556,
            KW: 679,
            IQD: 2191,
            LBP: 2191,
            default: 8064,
          },
          cardType: "cart",
          autoIntelRPOCode: "L87",
          brandItemCode: "OP33",
        },
        aii5h9cgyg19: {
          label: {
            en: "Heated Steering Wheel",
            ar: "خيار عجلة القيادة القابلة للتدفئة",
          },
          image: "images/2368/Heated_Steering_Wheeel_AT4.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 494,
            UAE: 485,
            OMR: 51,
            BHD: 50,
            QAR: 480,
            JOD: 94,
            KW: 41,
            IQD: 132,
            LBP: 132,
            default: 485,
          },
          cardType: "cart",
          autoIntelRPOCode: "KI3",
          brandItemCode: "AC190",
        },
        yfq9p87hxkkm: {
          label: {
            en: '20" Machined and Painted Talos Bronze Metallic(Standard)',
            ar: "",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          brandItemCode: "OP31",
        },
        yb3bv13k1d9s: {
          label: {
            en: '22" Black Wheels',
            ar: "عجلات سوداء مقاس 22 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "2b3vjc62r4n2": {
          label: {
            en: "Yukon_Default_sill_plate_Cargo",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        uqkubhykimks: {
          label: {
            en: "Cargo Area Sill Plate",
            ar: "عتبة حيز الأمتعة",
          },
          image: "images/2368/Accessories/CargoSillPlate.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3Q",
          excludeFromRecommendation: true,
          brandItemCode: "AC13",
        },
        "0uw9h0rfwubw": {
          label: {
            en: "Emblems Black Illuminated Front Grille",
            ar: "شبك أمامي أسود مع شعارات مضيئة",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R88",
          brandItemCode: "AC171",
        },
        sphrkplutvpy: {
          label: {
            en: "LED Puddle Lights",
            ar: "مصابيح LED للإضاءة السفلية عند الأبواب",
          },
          image: "images/2368/Accessories/LED_Puddle_Lights.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "DXR",
          excludeFromRecommendation: true,
          brandItemCode: "AC195",
        },
        "2az8gut5k3kr": {
          label: {
            en: "Floor Mat-Front",
            ar: "سجادة أرضية أمامية",
          },
          image: "images/2368/Accessories/at4ult_Floor_Mat_Front.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "B58",
          excludeFromRecommendation: true,
          brandItemCode: "AC180",
        },
        kvvnaw7j04w5: {
          label: {
            en: "Black GMC Grille Emblems",
            ar: "شعارات شبك GMC باللون الأسود",
          },
          image: "images/2368/Accessories/at4ult_Black_GMC_Grille_Emblems.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "SFZ",
          brandItemCode: "AC154",
        },
        mfqdrqt5u93i: {
          label: {
            en: "Black Nameplates Yukon",
            ar: "شارات يوكون باللون الأسود",
          },
          image: "images/2368/Accessories/Black_Nameplates_Yukon.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "RIK",
          brandItemCode: "AC156",
        },
        "20717it6ugub": {
          label: {
            en: "Illuminated Sill Plates",
            ar: "عتبات مضاءة",
          },
          image: "images/2368/Accessories/ACC_Illuminated_Sill_Yukon.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "S0M",
          excludeFromRecommendation: true,
          brandItemCode: "AC23",
        },
        ygqi3eegxe6y: {
          label: {
            en: "Black Exhaust Tip",
            ar: "طرف عادم أسود",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 511,
            UAE: 501,
            OMR: 53,
            BHD: 52,
            QAR: 496,
            JOD: 97,
            KW: 42,
            IQD: 136,
            LBP: 136,
            default: 501,
          },
          cardType: "cart",
          autoIntelRPOCode: "VTA",
          brandItemCode: "AC24",
        },
        hcujfdn25hed: {
          label: {
            en: "WO_Night_Vision_AT4_ULT",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        t838x538uzvt: {
          label: {
            en: "Night Vision",
            ar: "نظام الرؤية الليلية",
          },
          image: "images/2368/Accessories/Optional_Night_Vision_AT4_ULT.jpg",
          category: {
            en: "Night Vision",
            ar: "نظام الرؤية الليلية",
          },
          price: {
            KSA: 6587,
            UAE: 6464,
            OMR: 685,
            BHD: 667,
            QAR: 6394,
            JOD: 1247,
            KW: 545,
            IQD: 1757,
            LBP: 1757,
            default: 6464,
          },
          cardType: "cart",
          autoIntelRPOCode: "UV3",
          brandItemCode: "OP30",
        },
        etqrtj2dywbf: {
          label: {
            en: "variant_parts",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        d1se9jwnr8q934: {
          label: {
            en: "6.2L EcoTec3 V8 engine",
            ar: "محرك EcoTec3 V8 سعة 6.2 لتر",
          },
          image: "",
          category: {
            en: "Performance",
            ar: "الأداء",
          },
          price: {
            KSA: 8218,
            UAE: 8064.24,
            OMR: 855,
            BHD: 833,
            QAR: 7977,
            JOD: 1556,
            KW: 679,
            LBP: 2191,
            IQD: 2191,
            default: 8064.24,
          },
          cardType: "cart",
          autoIntelRPOCode: "L87",
          brandItemCode: "OP33",
        },
        aii5h9cgyg20: {
          label: {
            en: "Heated Steering Wheel",
            ar: "خيار عجلة القيادة القابلة للتدفئة",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "KI3",
          brandItemCode: "AC190",
        },
        drhsknaig5v4: {
          label: {
            en: '20" Multi-Dimensional Polished Aluminium',
            ar: "20 بوصة من الألومنيوم المصقول متعدد الأبعاد",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 3623,
            UAE: 3555.46,
            OMR: 377,
            BHD: 367,
            QAR: 3517,
            JOD: 686,
            KW: 300,
            LBP: 966,
            IQD: 966,
            default: 3555.46,
          },
          cardType: "cart",
        },
        ll389hjomzmw: {
          label: {
            en: '22" Ultra-Bright Machined and Painted Midnight Silver',
            ar: "22 إنش، فضي داكن لامع، مشغولة آلياً ومطلية",
          },
          image: "images/2368/Accessories/denali_UltraBright_Machined_Pained_Midnight.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 5302,
            UAE: 5203,
            OMR: 551,
            BHD: 537,
            QAR: 5146,
            JOD: 1004,
            KW: 438,
            IQD: 1414,
            LBP: 1414,
            default: 5203,
          },
          cardType: "cart",
          autoIntelRPOCode: "SMI",
          brandItemCode: "OP25",
        },
        "6r8odbd02l8r": {
          label: {
            en: '20" Polished Aluminium',
            ar: "ألومنيوم مصقول مقاس 20 إنش",
          },
          image: "images/2368/Accessories/denali_Polished_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          cardType: "cart",
          autoIntelRPOCode: "RTL",
          brandItemCode: "AC131",
        },
        fht84o5uu46k: {
          label: {
            en: '24" Selective Machined and Painted Pearl Nickel',
            ar: "24 إنش من النيكل اللؤلؤي المصقول والمطلي بشكل انتقائي",
          },
          image: "images/2368/Accessories/denali_Selective_Machined_Painted_Pearl_Nickel.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 10705,
            UAE: 10505,
            OMR: 1113,
            BHD: 1085,
            QAR: 10391,
            JOD: 2027,
            KW: 885,
            IQD: 2855,
            LBP: 2855,
            default: 10505,
          },
          cardType: "cart",
          autoIntelRPOCode: "RF3",
          brandItemCode: "OP27",
        },
        "1t3oatg17og0": {
          label: {
            en: '22" Carbon Flash Metallic Aluminium W/Selective Machining',
            ar: "ألومنيوم معدني لامع كربوني مقاس 22 إنش مع معالجة انتقائية",
          },
          image: "images/2368/Accessories/denali_Carbon_Flash_Metallic_Aluminum.jpg",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          cardType: "cart",
          autoIntelRPOCode: "SIY",
          brandItemCode: "AC135",
        },
        wdbc64xihtb2: {
          label: {
            en: '22" Black Wheels',
            ar: "عجلات سوداء مقاس 22 إنش",
          },
          image: "",
          category: {
            en: "Alloy Wheels",
            ar: "عجلات معدنية",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "0tf9b3v2ow0e": {
          label: {
            en: "Yukon_Default_sill_plate_Cargo",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        "0k89247b1ar5": {
          label: {
            en: "Cargo Area Sill Plate",
            ar: "عتبة حيز الأمتعة",
          },
          image: "images/2368/Accessories/CargoSillPlate.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "C3Q",
          excludeFromRecommendation: true,
          brandItemCode: "AC13",
        },
        mruisrltvi41: {
          label: {
            en: "Default_Mid_Bench_Seat_Denali",
            ar: "",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        qb16qdm7gr1x: {
          label: {
            en: "Second Row Bucket Power config Seats",
            ar: "مقاعد فردية كهربائية قابلة للتعديل في الصف الثاني",
          },
          image: "images/2368/Accessories/Optional_Mid_Captain_Seat_Denali.jpg",
          category: {
            en: "Seats",
            ar: "المقاعد",
          },
          price: {
            KSA: 2619,
            UAE: 2570,
            OMR: 272,
            BHD: 265,
            QAR: 2542,
            JOD: 496,
            KW: 216,
            IQD: 698,
            LBP: 698,
            default: 2570,
          },
          cardType: "cart",
          autoIntelRPOCode: "A50",
          brandItemCode: "AC210",
        },
        "6kqqviaek2io": {
          label: {
            en: "Emblems Black Illuminated Front Grille",
            ar: "شبك أمامي أسود مع شعارات مضيئة",
          },
          image: "images/2368/Accessories/denali_Emblems_Black_Illuminated_Front_Grille.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "R88",
          brandItemCode: "AC171",
        },
        "9emt18hb2kh7": {
          label: {
            en: "LED Puddle Lights",
            ar: "مصابيح LED للإضاءة السفلية عند الأبواب",
          },
          image: "images/2368/Accessories/LED_Puddle_Lights.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "DXR",
          excludeFromRecommendation: true,
          brandItemCode: "AC195",
        },
        wc691tqh2727: {
          label: {
            en: "Floor Mat-Front",
            ar: "سجادة أرضية أمامية",
          },
          image: "images/2368/Accessories/denali_Floor_Mat_Front.jpg",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "B58",
          excludeFromRecommendation: true,
          brandItemCode: "AC180",
        },
        mdtdtacp2vnx: {
          label: {
            en: "Illuminated Sill Plates",
            ar: "عتبات مضاءة",
          },
          image: "images/2368/Accessories/ACC_Illuminated_Sill_Denali.png",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "S0M",
          excludeFromRecommendation: true,
          brandItemCode: "AC23",
        },
        "27l8cstufhje": {
          label: {
            en: "Black Exhaust Tip",
            ar: "طرف عادم أسود",
          },
          image: "",
          category: {
            en: "Accessories",
            ar: "الملحقات",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
          autoIntelRPOCode: "VTA",
          brandItemCode: "AC24",
        },
        fmniir2z3toa: {
          label: {
            en: "variant_parts_ext",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
        bl456utej9ez: {
          label: {
            en: "variant_parts_int",
            ar: "",
          },
          image: "",
          category: {
            en: "Package",
            ar: "الفئة",
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: "cart",
        },
      },
      packages: {
        lrh1h0r2qg1c: {
          label: {
            en: "Max Trailering Package",
            ar: "فئة الحد الأقصى للقطر",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 1270,
            UAE: 1246,
            OMR: 132,
            BHD: 129,
            QAR: 1232,
            JOD: 240,
            KW: 105,
            IQD: 339,
            LBP: 339,
            default: 1246,
          },
          cardType: "cart",
          packageDescription: {
            en: "The Max Trailering Package offers Extra Cooling capacity with Hitch View and Integrated Trailer Brake.",
            ar: "توفر فئة الحد الأقصى للقطر قدرة تبريد إضافية مع كاميرا لرؤية وصلة القطر ونظام فرامل المقطورة المدمج.",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: "Extra Cooling Capacity",
                ar: "قدرة تبريد إضافية",
              },
              accessoryDescription: {
                en: "Extra Cooling Capacity",
                ar: "قدرة تبريد إضافية",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 92,
                UAE: 90,
                OMR: 10,
                BHD: 9,
                QAR: 89,
                JOD: 17,
                KW: 8,
                LBP: 24,
                IQD: 24,
                default: 90,
              },
            },
            {
              accessoryTitle: {
                en: "ProGrade Trailering System",
                ar: "نظام ProGrade للقطر",
              },
              accessoryDescription: {
                en: "Consist of Hitch View and Smart Trailer Integration Indicator",
                ar: "تتكون من عرض وصلة القطر ومؤشر تكامل المقطورة الذكي",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                LBP: 123,
                IQD: 123,
                default: 452,
              },
            },
            {
              accessoryTitle: {
                en: "Integrated Trailer Brake",
                ar: "نظام فرامل المقطورة المدمج",
              },
              accessoryDescription: {
                en: "Integrated Trailer Brake",
                ar: "نظام فرامل المقطورة المدمج",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                LBP: 191,
                IQD: 191,
                default: 704,
              },
            },
          ],
          autoIntelRPOCode: "NHT",
          brandItemCode: "PA02",
        },
        "0ybibmywl8w8": {
          label: {
            en: "Technology Package",
            ar: "فئة التكنولوجيا",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 6965,
            UAE: 6835,
            OMR: 724,
            BHD: 706,
            QAR: 6761,
            JOD: 1319,
            KW: 576,
            IQD: 1857,
            LBP: 1857,
            default: 6835,
          },
          cardType: "cart",
          packageDescription: {
            en: "The Technology Package includes Heated & Ventilated Front Seats with Rear Camera Mirror and Heads-Up Display",
            ar: "تتضمن فئة التكنولوجيا مقاعد أمامية قابلة للتدفئة والتهوية مع مرآة كاميرا خلفية وشاشة عرض علوية أمامية.",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: "Heated & Ventilated Front Seats",
                ar: "مقاعد أمامية قابلة للتدفئة والتهوية",
              },
              accessoryDescription: {
                en: "Heated & Ventilated Front Seats",
                ar: "مقاعد أمامية قابلة للتدفئة والتهوية",
              },
              image: "images/2368/Features/elevation_Seats.jpg",
              price: {
                KSA: 2141,
                UAE: 2101,
                OMR: 223,
                BHD: 217,
                QAR: 2078,
                JOD: 405,
                KW: 177,
                LBP: 571,
                IQD: 571,
                default: 2101,
              },
            },
            {
              accessoryTitle: {
                en: "Rear Camera Mirror",
                ar: "مرآة الكاميرا الخلفية",
              },
              accessoryDescription: {
                en: "Rear Camera Mirror",
                ar: "مرآة الكاميرا الخلفية",
              },
              image: "images/2368/Packages/W_Technology_Package_Elevation_1.jpg",
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                LBP: 417,
                IQD: 417,
                default: 1535,
              },
            },
            {
              accessoryTitle: {
                en: "Heads-Up Display",
                ar: "شاشة عرض علوية أمامية",
              },
              accessoryDescription: {
                en: "Heads-Up Display",
                ar: "شاشة عرض علوية أمامية",
              },
              image: "images/2368/Packages/W_Technology_Package_Elevation_2.jpg",
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                LBP: 702,
                IQD: 702,
                default: 2585,
              },
            },
            {
              accessoryTitle: {
                en: "4-way Front driver and Passenger power lumbar",
                ar: "مسند أسفل الظهر كهربائي للسائق والراكب الأمامي بأربع وضعيات",
              },
              accessoryDescription: {
                en: "4-way Front driver and Passenger power lumbar",
                ar: "مسند أسفل الظهر كهربائي للسائق والراكب الأمامي بأربع وضعيات",
              },
              image: "images/2368/Packages/Lumbar_Elevation.jpg",
              price: {
                KSA: 626,
                UAE: 614,
                OMR: 65,
                BHD: 63,
                QAR: 607,
                JOD: 118,
                KW: 52,
                LBP: 167,
                IQD: 167,
                default: 614,
              },
            },
          ],
          autoIntelRPOCode: "CWM",
          brandItemCode: "PA03",
        },
        "1bxd2a82m8zj": {
          label: {
            en: "Black Out Package",
            ar: "باقة اللون الأسود",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 9497,
            UAE: 9320,
            OMR: 988,
            BHD: 962,
            QAR: 9219,
            JOD: 1798,
            KW: 785,
            IQD: 2533,
            LBP: 2533,
            default: 9320,
          },
          cardType: "cart",
          packageDescription: {
            en: 'The Black Out Package features 22" Multi-spoke Gloss Black wheels, Black GMC Grille Emblems and Black Name Plates',
            ar: "تتميز باقة اللون الأسود بعجلات سوداء لامعة متعددة الأضلاع مقاس 22 إنش وشعارات GMC السوداء والشارات السوداء",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '22" Multi-spoke Gloss Black wheels',
                ar: "عجلات متعددة الأضلاع مقاس 22 إنش بلون أسود لامع",
              },
              accessoryDescription: {
                en: '22" Multi-spoke Gloss Black wheels',
                ar: "عجلات متعددة الأضلاع مقاس 22 إنش بلون أسود لامع",
              },
              image: "images/2368/Accessories/inchBlackWheelsYukon.jpg",
              price: {
                KSA: 8967,
                UAE: 8800,
                OMR: 933,
                BHD: 909,
                QAR: 8704,
                JOD: 1698,
                KW: 741,
                LBP: 2391,
                IQD: 2391,
                default: 8800,
              },
            },
            {
              accessoryTitle: {
                en: "Black GMC Grille Emblems",
                ar: "شعارات شبك GMC باللون الأسود",
              },
              accessoryDescription: {
                en: "Black GMC Grille Emblems",
                ar: "شعارات شبك GMC باللون الأسود",
              },
              image: "images/2368/Accessories/elevation_Black_GMC_Grille_Emblems.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Black Nameplates",
                ar: "شارات باللون الأسود",
              },
              accessoryDescription: {
                en: "Black Nameplates",
                ar: "شارات باللون الأسود",
              },
              image: "images/2368/Accessories/Black_Nameplates_Yukon.jpg",
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                LBP: 141,
                IQD: 141,
                default: 520,
              },
            },
          ],
          autoIntelRPOCode: "RGF",
          brandItemCode: "PA16",
        },
        a1lyjpkbd835: {
          label: {
            en: "Max Trailering Package",
            ar: "فئة الحد الأقصى للقطر",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 1270,
            UAE: 1246,
            OMR: 132,
            BHD: 129,
            QAR: 1232,
            JOD: 240,
            KW: 105,
            IQD: 339,
            LBP: 339,
            default: 1246,
          },
          cardType: "cart",
          packageDescription: {
            en: "The Max Trailering Package offers Extra Cooling capacity with Hitch View and Integrated Trailer Brake.",
            ar: "توفر فئة الحد الأقصى للقطر قدرة تبريد إضافية مع كاميرا لرؤية وصلة القطر ونظام فرامل المقطورة المدمج.",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: "Extra Cooling Capacity",
                ar: "قدرة تبريد إضافية",
              },
              accessoryDescription: {
                en: "Extra Cooling Capacity",
                ar: "قدرة تبريد إضافية",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 92,
                UAE: 90,
                OMR: 10,
                BHD: 9,
                QAR: 89,
                JOD: 17,
                KW: 8,
                LBP: 24,
                IQD: 24,
                default: 90,
              },
            },
            {
              accessoryTitle: {
                en: "ProGrade Trailering System",
                ar: "نظام ProGrade للقطر",
              },
              accessoryDescription: {
                en: "Consist of Hitch View and Smart Trailer Integration Indicator",
                ar: "تتكون من عرض وصلة القطر ومؤشر تكامل المقطورة الذكي",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                LBP: 123,
                IQD: 123,
                default: 452,
              },
            },
            {
              accessoryTitle: {
                en: "Integrated Trailer Brake",
                ar: "نظام فرامل المقطورة المدمج",
              },
              accessoryDescription: {
                en: "Integrated Trailer Brake",
                ar: "نظام فرامل المقطورة المدمج",
              },
              image: "images/2368/Packages/MaxTraileringPackage_Elevation.jpg",
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                LBP: 191,
                IQD: 191,
                default: 704,
              },
            },
          ],
          autoIntelRPOCode: "NHT",
          brandItemCode: "PA02",
        },
        djmo7f5glzeo: {
          label: {
            en: "Technology Package",
            ar: "فئة التكنولوجيا",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 6965,
            UAE: 6835,
            OMR: 724,
            BHD: 706,
            QAR: 6761,
            JOD: 1319,
            KW: 576,
            IQD: 1857,
            LBP: 1857,
            default: 6835,
          },
          cardType: "cart",
          packageDescription: {
            en: "The Technology Package includes Heated & Ventilated Front Seats with Rear Camera Mirror and Heads-Up Display",
            ar: "تتضمن فئة التكنولوجيا مقاعد أمامية قابلة للتدفئة والتهوية مع مرآة كاميرا خلفية وشاشة عرض علوية أمامية.",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: "Heated & Ventilated Front Seats",
                ar: "مقاعد أمامية قابلة للتدفئة والتهوية",
              },
              accessoryDescription: {
                en: "Heated & Ventilated Front Seats",
                ar: "مقاعد أمامية قابلة للتدفئة والتهوية",
              },
              image: "images/2368/Features/elevation_Seats.jpg",
              price: {
                KSA: 2141,
                UAE: 2101,
                OMR: 223,
                BHD: 217,
                QAR: 2078,
                JOD: 405,
                KW: 177,
                LBP: 571,
                IQD: 571,
                default: 2101,
              },
            },
            {
              accessoryTitle: {
                en: "Rear Camera Mirror",
                ar: "مرآة الكاميرا الخلفية",
              },
              accessoryDescription: {
                en: "Rear Camera Mirror",
                ar: "مرآة الكاميرا الخلفية",
              },
              image: "images/2368/Packages/W_Technology_Package_Elevation_1.jpg",
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                LBP: 417,
                IQD: 417,
                default: 1535,
              },
            },
            {
              accessoryTitle: {
                en: "Heads-Up Display",
                ar: "شاشة عرض علوية أمامية",
              },
              accessoryDescription: {
                en: "Heads-Up Display",
                ar: "شاشة عرض علوية أمامية",
              },
              image: "images/2368/Packages/W_Technology_Package_Elevation_2.jpg",
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                LBP: 702,
                IQD: 702,
                default: 2585,
              },
            },
            {
              accessoryTitle: {
                en: "4-way Front driver and Passenger power lumbar",
                ar: "مسند أسفل الظهر كهربائي للسائق والراكب الأمامي بأربع وضعيات",
              },
              accessoryDescription: {
                en: "4-way Front driver and Passenger power lumbar",
                ar: "مسند أسفل الظهر كهربائي للسائق والراكب الأمامي بأربع وضعيات",
              },
              image: "images/2368/Packages/Lumbar_Elevation.jpg",
              price: {
                KSA: 626,
                UAE: 614,
                OMR: 65,
                BHD: 63,
                QAR: 607,
                JOD: 118,
                KW: 52,
                LBP: 167,
                IQD: 167,
                default: 614,
              },
            },
          ],
          autoIntelRPOCode: "CWM",
          brandItemCode: "PA03",
        },
        djmo7f5glzeo243: {
          label: {
            en: "Black Out Package",
            ar: "باقة اللون الأسود",
          },
          image: "images/2368/Packages/Elevation.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 9497,
            UAE: 9320,
            OMR: 988,
            BHD: 962,
            QAR: 9219,
            JOD: 1798,
            KW: 785,
            IQD: 2533,
            LBP: 2533,
            default: 9320,
          },
          cardType: "cart",
          packageDescription: {
            en: 'The Black Out Package features 22" Multi-spoke Gloss Black wheels, Black GMC Grille Emblems and Black Name Plates',
            ar: "تتميز باقة اللون الأسود بعجلات سوداء لامعة متعددة الأضلاع مقاس 22 إنش وشعارات GMC السوداء والشارات السوداء",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '22" Multi-spoke Gloss Black wheels',
                ar: "عجلات متعددة الأضلاع مقاس 22 إنش بلون أسود لامع",
              },
              accessoryDescription: {
                en: '22" Multi-spoke Gloss Black wheels',
                ar: "عجلات متعددة الأضلاع مقاس 22 إنش بلون أسود لامع",
              },
              image: "images/2368/Accessories/inchBlackWheelsYukon.jpg",
              price: {
                KSA: 8967,
                UAE: 8800,
                OMR: 933,
                BHD: 909,
                QAR: 8704,
                JOD: 1698,
                KW: 741,
                LBP: 2391,
                IQD: 2391,
                default: 8800,
              },
            },
            {
              accessoryTitle: {
                en: "Black GMC Grille Emblems",
                ar: "شعارات شبك GMC باللون الأسود",
              },
              accessoryDescription: {
                en: "Black GMC Grille Emblems",
                ar: "شعارات شبك GMC باللون الأسود",
              },
              image: "images/2368/Accessories/elevation_Black_GMC_Grille_Emblems.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Black Nameplates",
                ar: "شارات باللون الأسود",
              },
              accessoryDescription: {
                en: "Black Nameplates",
                ar: "شارات باللون الأسود",
              },
              image: "images/2368/Accessories/Black_Nameplates_Yukon.jpg",
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                LBP: 141,
                IQD: 141,
                default: 520,
              },
            },
          ],
          autoIntelRPOCode: "RGF",
          brandItemCode: "PA16",
        },
        x7vtyrufcgr0: {
          label: {
            en: "Advanced Security Package",
            ar: "فئة الأمان المتقدمة",
          },
          image: "images/2368/Packages/AT4.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 313,
            UAE: 307,
            OMR: 33,
            BHD: 32,
            QAR: 304,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 307,
          },
          cardType: "cart",
          packageDescription: {
            en: "The Advance Security package offers Self- Powered Horn, Interior Movement Sensors, Vehicle Inclination Sensors with Glass Break Sensors in Rear Quarter Glass and Liftgate Window and Door and Liftgate Lock Shields.",
            ar: "توفر فئة الأمان المتقدمة بوقًا ذاتي الطاقة، وأجهزة استشعار للحركة الداخلية، وأجهزة استشعار ميل المركبة مع أجهزة استشعار كسر الزجاج في الزجاج الجانبي الخلفي ونافذة الباب الخلفي، مع دروع واقية لأقفال الأبوب والباب الخلفي.",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: "Self- Powered Horn",
                ar: "بوق ذاتي الطاقة",
              },
              accessoryDescription: {
                en: "Self- Powered Horn",
                ar: "بوق ذاتي الطاقة",
              },
              image: "images/2368/Packages/W_Advanced_Security_Pack_AT4.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Interior Movement Sensors",
                ar: "أجهزة استشعار الحركة الداخلية",
              },
              accessoryDescription: {
                en: "Interior Movement Sensors",
                ar: "أجهزة استشعار الحركة الداخلية",
              },
              image: "images/2368/Packages/W_Advanced_Security_Pack_AT4.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Vehicle Inclination Sensors",
                ar: "أجهزة استشعار ميل السيارة",
              },
              accessoryDescription: {
                en: "Vehicle Inclination Sensors",
                ar: "أجهزة استشعار ميل السيارة",
              },
              image: "images/2368/Packages/W_Advanced_Security_Pack_AT4.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Glass Break Sensors in Rear Quarter Glass and Liftgate Window and Door and Liftgate Lock Shields",
                ar: "أجهزة استشعار كسر الزجاج في الزجاج الجانبي الخلفي ونافذة الباب الخلفي، مع دروع واقية لأقفال الأبوب والباب الخلفي.",
              },
              accessoryDescription: {
                en: "Glass Break Sensors in Rear Quarter Glass and Liftgate Window and Door and Liftgate Lock Shields",
                ar: "أجهزة استشعار كسر الزجاج في الزجاج الجانبي الخلفي ونافذة الباب الخلفي، مع دروع واقية لأقفال الأبوب والباب الخلفي.",
              },
              image: "images/2368/Packages/W_Advanced_Security_Pack_AT4.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: "Theft-deterrent alarm system",
                ar: "نظام إنذار لمنع السرقة",
              },
              accessoryDescription: {
                en: "Theft-deterrent alarm system",
                ar: "نظام إنذار لمنع السرقة",
              },
              image: "images/2368/Packages/W_Advanced_Security_Pack_AT4.jpg",
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: "NWM",
          brandItemCode: "PA06",
        },
        sd9rqxud0aff: {
          label: {
            en: "Technology Package",
            ar: "فئة التكنولوجيا",
          },
          image: "images/2368/Packages/AT4.jpg",
          category: {
            en: "Packages",
            ar: "الفئات",
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          cardType: "cart",
          packageDescription: {
            en: 'The Technology Package includes 15" Heads-Up Display and Light Sensitive + Full Video Inside Rear View Mirror',
            ar: "تتضمن حزمة التكنولوجيا شاشة عرض علوية أمامية مقاس 15 إنش، ومرآة رؤية خلفية ذاتية التعتيم + عرض فيديو مباشر داخل المرآة من الكاميرا الخلفية",
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '15" Heads-Up Display',
                ar: "شاشة عرض أمامية مقاس 15 انش",
              },
              accessoryDescription: {
                en: '15" Heads-Up Display',
                ar: "شاشة عرض أمامية مقاس 15 انش",
              },
              image: "images/2368/Packages/W_Technology_Package_AT4_1.jpg",
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                LBP: 702,
                IQD: 702,
                default: 2585,
              },
            },
            {
              accessoryTitle: {
                en: "Light Sensitive + Full Video Inside Rear View Mirror",
                ar: "مرآة رؤية خلفية ذاتية التعتيم + عرض فيديو مباشر داخل المرآة من الكاميرا الخلفية",
              },
              accessoryDescription: {
                en: "Light Sensitive + Full Video Inside Rear View Mirror",
                ar: "مرآة رؤية خلفية ذاتية التعتيم + عرض فيديو مباشر داخل المرآة من الكاميرا الخلفية",
              },
              image: "images/2368/Packages/W_Technology_Package_AT4_2.jpg",
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                LBP: 417,
                IQD: 417,
                default: 1535,
              },
            },
          ],
          autoIntelRPOCode: "CWM",
          brandItemCode: "PA03",
        },
      },
      interactions: {
        q4efn17j0xpb: {
          label: {
            en: "Doors",
            ar: "الأبواب",
          },
          type: "interaction",
        },
        cid63atonu3q: {
          label: {
            en: "Boot",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        "8y1ygewkzqwp": {
          label: {
            en: "Sunroof",
            ar: "فتحة السقف",
          },
          type: "interaction",
        },
        xgeya76hww4y: {
          label: {
            en: "Headlight",
            ar: "المصابيح الأمامية",
          },
          type: "interaction",
        },
        z4fmgvbj6vl6: {
          label: {
            en: "DRL",
            ar: "DRL",
          },
          type: "interaction",
        },
        j9phsdy1vqre: {
          label: {
            en: "Taillight",
            ar: "الضوء الخلفي",
          },
          type: "interaction",
        },
        agvzuwuqtkbm: {
          label: {
            en: "Fog Lamps",
            ar: "مصابيح الضباب",
          },
          type: "interaction",
        },
        ddqoq36ys5nl: {
          label: {
            en: "3nd Row Folding Seat",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        aa191tcji6do: {
          label: {
            en: "Doors",
            ar: "الأبواب",
          },
          type: "interaction",
        },
        xdvvqsba76gn: {
          label: {
            en: "Boot",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        ixx09qpwupoa: {
          label: {
            en: "Sunroof",
            ar: "فتحة السقف",
          },
          type: "interaction",
        },
        leogf3jjdxoa: {
          label: {
            en: "Headlight",
            ar: "المصابيح الأمامية",
          },
          type: "interaction",
        },
        r9lchu55ok3e: {
          label: {
            en: "DRL",
            ar: "DRL",
          },
          type: "interaction",
        },
        w7xsrj20ro3z: {
          label: {
            en: "Taillight",
            ar: "الضوء الخلفي",
          },
          type: "interaction",
        },
        qldvhe66tc3c: {
          label: {
            en: "Fog Lamps",
            ar: "مصابيح الضباب",
          },
          type: "interaction",
        },
        grshgv2iww8c: {
          label: {
            en: "3nd Row Folding Seat",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        "3gjt48jevm9g": {
          label: {
            en: "Doors",
            ar: "الأبواب",
          },
          type: "interaction",
        },
        v3p6hnkfk4sx: {
          label: {
            en: "Boot",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        flbo1iqfna62: {
          label: {
            en: "Sunroof",
            ar: "فتحة السقف",
          },
          type: "interaction",
        },
        pptw14tjetcr: {
          label: {
            en: "Headlight",
            ar: "المصابيح الأمامية",
          },
          type: "interaction",
        },
        vmbjveuli0by: {
          label: {
            en: "DRL",
            ar: "DRL",
          },
          type: "interaction",
        },
        "29fpskrobqlq": {
          label: {
            en: "Taillight",
            ar: "الضوء الخلفي",
          },
          type: "interaction",
        },
        c2s35fgsuiuu: {
          label: {
            en: "Fog Lamps",
            ar: "مصابيح الضباب",
          },
          type: "interaction",
        },
        o1jxfnah7tvf: {
          label: {
            en: "3nd Row Folding Seat",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        "9oajgf16euy4": {
          label: {
            en: "Doors",
            ar: "الأبواب",
          },
          type: "interaction",
        },
        lltey55ix5y7: {
          label: {
            en: "Boot",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        b7kz4mrf26g2: {
          label: {
            en: "Sunroof",
            ar: "فتحة السقف",
          },
          type: "interaction",
        },
        yfb8ezuliqph: {
          label: {
            en: "Headlight",
            ar: "المصابيح الأمامية",
          },
          type: "interaction",
        },
        p4pudpriur0z: {
          label: {
            en: "DRL",
            ar: "DRL",
          },
          type: "interaction",
        },
        tdcwq6semoqw: {
          label: {
            en: "Taillight",
            ar: "الضوء الخلفي",
          },
          type: "interaction",
        },
        kxr3vvny876a: {
          label: {
            en: "Fog Lamps",
            ar: "مصابيح الضباب",
          },
          type: "interaction",
        },
        "08h3pf8il71l": {
          label: {
            en: "3nd Row Folding Seat",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        xbdzchklx04d: {
          label: {
            en: "Doors",
            ar: "الأبواب",
          },
          type: "interaction",
        },
        yz2djr3m8zor: {
          label: {
            en: "Boot",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
        fwou2afhghpp: {
          label: {
            en: "Sunroof",
            ar: "فتحة السقف",
          },
          type: "interaction",
        },
        "5g2eoomyui58": {
          label: {
            en: "Headlight",
            ar: "المصابيح الأمامية",
          },
          type: "interaction",
        },
        "6146uzbd6kui": {
          label: {
            en: "DRL",
            ar: "DRL",
          },
          type: "interaction",
        },
        iq7p37hq4g0j: {
          label: {
            en: "Taillight",
            ar: "الضوء الخلفي",
          },
          type: "interaction",
        },
        "66sikckzjmb5": {
          label: {
            en: "Fog Lamps",
            ar: "مصابيح الضباب",
          },
          type: "interaction",
        },
        vb0nbeis8kuj: {
          label: {
            en: "3nd Row Folding Seat",
            ar: "صندوق المركبة",
          },
          type: "interaction",
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: "Exterior",
            ar: "الخارج",
          },
        },
        interiorCamera: {
          title: {
            en: "First Row Seat",
            ar: "مقعد الصف الأول",
          },
        },
        interiorCamera_1: {
          title: {
            en: "Second Row Seat",
            ar: "مقعد الصف الثاني",
          },
        },
        interiorCamera_2: {
          title: {
            en: "Third Row Seat",
            ar: "مقعد الصف الثالث",
          },
        },
      },
      name: {
        en: "GMC Yukon",
        ar: "جي إم سي يوكن",
      },
      specifications: {
        heading: {
          en: "GMC Yukon",
          ar: "جي إم سي يوكن",
        },
        trim: {
          en: "Denali",
          ar: "Denali",
        },
        price: {
          en: "Starting from 346200 MSRP",
          ar: "ابتداء من 346200 MSRP",
        },
        model: "GMC Yukon",
        seatingCapacity: "8",
        dimensions: {
          length: "5334 mm",
          width: "",
          height: "",
          wheelbase: "3071 mm",
          groundClearance: "",
        },
        performance: [
          {
            value: "355/420 HP",
            unit: "",
            label: {
              en: "Horsepower",
              ar: "حصان",
            },
          },
          {
            value: "518/624 NM",
            unit: "",
            label: {
              en: "Torque",
              ar: "عزم الدوران",
            },
          },
          {
            value: "5.3L/6.2L",
            unit: "",
            label: {
              en: "V8 Engine",
              ar: "V8 محرك",
            },
          },
        ],
        summary: [
          {
            title: {
              en: "Model",
              ar: "نموذج",
            },
            subtitle: {
              en: "GMC Yukon",
              ar: "GMC Yukon",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Exterior Colors",
              ar: "الألوان الخارجية",
            },
            subtitle: {
              en: "White Frost Tricoat, Titanium Rush Metallic, Summit White, Onyx Black, Volcanic Red Tintcoat, Sterling Metallic, Downpour Metallic",
              ar: "أبيض ثلجي بثلاث طبقات، تيتانيوم راش معدني، أبيض، أسود أونيكس، أحمر بركاني بطبقتين، ستيرلينج معدني، مطري معدني",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Interior",
              ar: "الداخلية",
            },
            subtitle: {
              en: "Dark Atmosphere/Gideon, Forest Storm with Mahogany Accents, Obsidian Rush with Red Accents, Jet Black Teak/Light Shale",
              ar: "بيج داكن/جيديون، زيتي داكن مع لمسات الماهوغني، أوبسيديان راش مع لمسات حمراء، خشب الساج الأسود / صلصالي فاتح",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Wheelbase",
              ar: "قاعدة العجلات",
            },
            subtitle: {
              en: "3071 mm",
              ar: "3071 ملم",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Length",
              ar: "الطول",
            },
            subtitle: {
              en: "5334 mm",
              ar: "  ٥٣٣٤ ملم",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Cargo Space",
              ar: "حيز الأمتعة",
            },
            subtitle: {
              en: "Behind 1st row seat- 3477.7 litres, Behind 2nd row seat- 2056 litres, Behind 3rd row seat- 722.2 liters",
              ar: "خلف مقعد الصف الأول - 3477.7 لتر، خلف مقعد الصف الثاني - 2056 لتر، خلف مقعد الصف الثالث - 722.2 لتر",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Transmission",
              ar: "نقل الحركة",
            },
            subtitle: {
              en: "10-speed Automatic",
              ar: "ناقل حركة أوتوماتيكي بـ 10 سرعات",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "OnStar Basics",
              ar: "باقة اونستار الأساسية",
            },
            subtitle: {
              en: "8 Years",
              ar: "8 سنوات",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "OnStar One",
              ar: "باقة اونستار ون",
            },
            subtitle: {
              en: "Upto 3 Years",
              ar: "حتى 3 سنوات",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Seating Capacity",
              ar: "عدد الركاب",
            },
            subtitle: {
              en: "8",
              ar: "8",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: "Infotainment Screen",
              ar: "شاشة المعلومات والترفيه",
            },
            subtitle: {
              en: "16.8 Inch Diagonal Center Touch-Screen",
              ar: "شاشة مركزية تعمل باللمس قطر 16.8 إنش",
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
      accessoryCategories: {
        exterior: ["Roof", "Alloy Wheels"],
        interior: ["Seats"],
        options: ["Accessories", "Night Vision", "Performance"],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: "Model",
        ar: "نموذج",
      },
      exteriorColours: {
        en: "Exterior",
        ar: "الخارجة",
      },
      interiorAmbience: {
        en: "Interior",
        ar: "الداخلية",
      },
      seatingCapacity: {
        en: "Seating Capacity",
        ar: "سعة المقاعد",
      },
      length: {
        en: "Length",
        ar: "الطول",
      },
      width: {
        en: "Width",
        ar: "العرض",
      },
      height: {
        en: "Height",
        ar: "الارتفاع",
      },
      wheelbase: {
        en: "Wheelbase",
        ar: "قاعدة العجلات",
      },
      groundClearance: {
        en: "Ground Clearance",
        ar: "الخلوص الأرضي",
      },
      share: {
        en: "Share",
        ar: "يشارك",
      },
    },
    variantSelection: {
      title: {
        en: "Model",
        ar: "نموذج",
      },
      select: {
        en: "Select",
        ar: "تحديد",
      },
      cancel: {
        en: "Cancel",
        ar: "إلغاء",
      },
      selectBodyStyle: {
        en: "Select Body Style",
        ar: "اختر نمط الهيكل",
      },
      availableTrims: {
        en: "Select Trim",
        ar: "اختر الفئة",
      },
      driveType: {
        en: "Drive Type",
        ar: "نوع القيادة",
      },
      model: {
        en: "Model",
        ar: "الموديل",
      },
    },
    loadingScreen: {
      default: {
        en: "Preparing the canvas for your dream car. Just a few more seconds to go!",
        ar: "لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!",
      },
      enterConfigurator: {
        en: "Build Your Ride",
        ar: "ابنِ سيارتك",
      },
      loading: {
        en: "Loading",
        ar: "جارٍ التحميل",
      },
      variantLoading: {
        en: "Trim is loading",
        ar: "نموذج جارٍ التحميل",
      },
      viewLoading: {
        en: "Applying final touches",
        ar: "تطبيق اللمسات النهائية",
      },
      disclaimer: {
        en: "Disclaimer",
        ar: "تنصل",
      },
      termsConditions: {
        en: "Terms & Conditions",
        ar: "الشروط والأحكام",
      },
      viewSpecDetails: {
        en: "View Spec Details",
        ar: "ععرض تفاصيل المواصفات",
      },
      disclaimerContent: {
        en: "Prices are subject to change without prior notice and may vary based on location, availability, or other factors.",
        ar: "الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.",
      },
      termsModal: {
        title: {
          en: "Configurator Use Terms and Conditions",
          ar: "شروط وأحكام استخدام المُكوِّن",
        },
        lead: {
          en: "By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:",
          ar: "من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:",
        },
        sections: {
          item1: {
            title: {
              en: "Non-Binding Submission",
              ar: "تقديم غير ملزم",
            },
            content: {
              en: "Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.",
              ar: "إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.",
            },
          },
          item2: {
            title: {
              en: "Preliminary Estimate",
              ar: "تقدير أولي",
            },
            content: {
              en: "All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.",
              ar: "جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.",
            },
          },
          item3: {
            title: {
              en: "Dealer Follow-Up",
              ar: "متابعة التاجر",
            },
            content: {
              en: "Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.",
              ar: "سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.",
            },
          },
          item4: {
            title: {
              en: "Availability and Fulfillment",
              ar: "التوفر والوفاء",
            },
            content: {
              en: "We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.",
              ar: "سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.",
            },
          },
          item5: {
            title: {
              en: "No Obligation",
              ar: "لا يوجد التزام",
            },
            content: {
              en: "You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.",
              ar: "لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.",
            },
          },
          item6: {
            title: {
              en: "Use of Personal Information and Consent",
              ar: "استخدام المعلومات الشخصية والموافقة",
            },
            content: {
              en: "By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].",
              ar: "من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.",
            },
          },
          item7: {
            title: {
              en: "System Limitations",
              ar: "قيود النظام",
            },
            content: {
              en: "While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.",
              ar: "مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.",
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: "Exit",
        ar: "خروج",
      },
      close: {
        en: "Close",
        ar: "إغلاق",
      },
      cancel: {
        en: "Cancel",
        ar: "إلغاء",
      },
      back: {
        en: "Back",
        ar: "خلف",
      },
      next: {
        en: "Next",
        ar: "التالي",
      },
      done: {
        en: "Done",
        ar: "تم",
      },
      exterior: {
        en: "Exterior",
        ar: "الخارجة",
      },
      interior: {
        en: "Interior",
        ar: "الداخلية",
      },
      enterFullscreen: {
        en: "Enter Fullscreen",
        ar: "ملء الشاشة",
      },
      exitFullscreen: {
        en: "Exit Fullscreen",
        ar: "إلغاء ملء الشاشة",
      },
      selectTrim: {
        en: "Select Trim",
        ar: "اختر الفئة",
      },
      viewConfiguration: {
        en: "View Configuration",
        ar: "عرض التكوين",
      },
      togglePanelTooltip: {
        en: "Select Trim and Configuration",
        ar: "اختر الفئة والتكوين",
      },
      nextStepTooltip: {
        en: "Click to proceed to the next step",
        ar: "انقر للانتقال إلى الخطوة التالية",
      },
      heading: {
        en: "Standard Features",
        ar: "المميزات  القياسية",
      },
      description: {
        en: "Chevrolet Arabia",
        ar: "شيفروليه العربية",
      },
      goBack: {
        en: "Go Back",
        ar: "العودة",
      },
      freeRoamView: {
        en: "Free Roam View",
        ar: "عرض التجوال الحر",
      },
      featureView: {
        en: "Featured View",
        ar: "عرض مميز",
      },
      configurator: {
        en: "Configurator",
        ar: "المُكوِّن",
      },
      buildYourRide: {
        en: "Build Your Ride",
        ar: "ابنِ سيارتك",
      },
      options: {
        en: "Options",
        ar: "الخيارات",
      },
      summary: {
        en: "Summary",
        ar: "الملخص",
      },
      saveYourBuild: {
        en: "Save Your Build",
        ar: "احفظ تكوينك",
      },
      startingPrice: {
        en: "Starting Price",
        ar: "السعر الابتدائي",
      },
      msrp: {
        en: "MSRP",
        ar: "سعر التجزئة المقترح",
      },
      proceed: {
        en: "Proceed",
        ar: "متابعة",
      },
    },
    tabs: {
      config: {
        en: "Config",
        ar: "الإعداد",
      },
      model: {
        en: "Model",
        ar: "الموديل",
      },
      exterior: {
        en: "Exterior",
        ar: "الخارجي",
      },
      interior: {
        en: "Interior",
        ar: "الداخلي",
      },
      options: {
        en: "Options",
        ar: "الخيارات",
      },
      summary: {
        en: "Summary",
        ar: "الملخص",
      },
    },
    cart: {
      title: {
        en: "Your Order",
        ar: "طلبك",
      },
      disclaimer: {
        en: "Accessory prices and availability will be confirmed at the showroom.",
        ar: "سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.",
      },
      exteriorLabel: {
        en: "Exterior:",
        ar: ":خارجي",
      },
      interiorLabel: {
        en: "Interior:",
        ar: ":داخلي",
      },
      addedAccessories: {
        en: "Added Accessories",
        ar: "الإكسسوارات المضافة",
      },
      priceEstimate: {
        en: "Price Estimate",
        ar: "السعر التقديري",
      },
      priceBreakdown: {
        en: "Price Breakdown",
        ar: "تفاصيل السعر",
      },
      reserveNow: {
        en: "Proceed",
        ar: "متابعة",
      },
      removeAccessory: {
        en: "Remove accessory",
        ar: "إزالة الإكسسوار",
      },
      removeItem: {
        en: "Remove",
        ar: "إزالة",
      },
      packageCategory: {
        en: "Package",
        ar: "حزمة",
      },
    },
    saveDialog: {
      optionsTitle: {
        en: "How would you like to proceed?",
        ar: "كيف تريد المتابعة؟",
      },
      optionWhatsapp: {
        en: "Save to WhatsApp",
        ar: "حفظ على واتساب",
      },
      optionEmail: {
        en: "Save to Email",
        ar: "حفظ على البريد الإلكتروني",
      },
      optionTestDrive: {
        en: "Book a Test Drive",
        ar: "احجز اختبار قيادة",
      },
      optionCallback: {
        en: "Request a call Back",
        ar: "طلب معاودة الاتصال",
      },
      labelFirstName: {
        en: "First Name*",
        ar: "الاسم الأول",
      },
      labelLastName: {
        en: "Last Name*",
        ar: "اسم العائلة",
      },
      labelEmail: {
        en: "Email*",
        ar: "البريد الالكتروني",
      },
      labelMobileNumber: {
        en: "Mobile Number*",
        ar: "رقم الجوال",
      },
      labelPrefix: {
        en: "Prefix",
        ar: "البادئة",
      },
      labelModel: {
        en: "Model",
        ar: "نموذج",
      },
      labelDealership: {
        en: "Preferred Dealership*",
        ar: "الوكيل المفضّل",
      },
      labelComments: {
        en: "Comments",
        ar: "تعليقات",
      },
      labelTestDriveDate: {
        en: "Preferred date*",
        ar: "التاريخ المفضل*",
      },
      labelTestDriveTime: {
        en: "Preferred time*",
        ar: "الوقت المفضل*",
      },
      placeholderTestDriveDate: {
        en: "Preferred date*",
        ar: "التاريخ المفضل*",
      },
      placeholderTestDriveTime: {
        en: "Preferred time*",
        ar: "الوقت المفضل*",
      },
      placeholderFirstName: {
        en: "First Name*",
        ar: "الاسم الأول",
      },
      placeholderLastName: {
        en: "Last Name*",
        ar: "اسم العائلة",
      },
      placeholderEmail: {
        en: "Email*",
        ar: "البريد الالكتروني",
      },
      placeholderMobileNumber: {
        en: "Mobile Number*",
        ar: "رقم الجوال",
      },
      placeholderComments: {
        en: "Comments",
        ar: "تعليقات",
      },
      placeholderSelectDealership: {
        en: "Preferred Dealership*",
        ar: "الوكيل المفضّل",
      },
      privacyTitle: {
        en: "Privacy statement & legal disclaimer",
        ar: "بيان الخصوصية وإخلاء المسؤولية القانونية",
      },
      privacyText: {
        en: "I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.",
        ar: "أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.",
      },
      privacyReadMore: {
        en: "Read more",
        ar: "اقرأ المزيد",
      },
      privacyReadMoreUrl: {
        en: "https://www.gmcarabia.com/ae-en/privacy",
        ar: "https://www.gmcarabia.com/ae-ar/privacy",
      },
      privacyMarketingConsent: {
        en: "I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.",
        ar: "أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية",
      },
      buttonSend: {
        en: "Send Configuration",
        ar: "إرسال التكوين",
      },
      buttonSending: {
        en: "Submitting...",
        ar: "تقديم...",
      },
      buttonSent: {
        en: "Sent!",
        ar: "مرسل!",
      },
      buttonRetry: {
        en: "Retry",
        ar: "إعادة المحاولة",
      },
      buttonClose: {
        en: "Okay, Got it",
        ar: "حسنًا، فهمت",
      },
      successTitle: {
        en: "Saved",
        ar: "محفوظ",
      },
      successLabelModel: {
        en: "Model Name",
        ar: "اسم النموذج",
      },
      successLabelVariant: {
        en: "Variant Name",
        ar: "اسم البديل",
      },
      successLabelName: {
        en: "Name",
        ar: "اسم",
      },
      successLabelEmail: {
        en: "Email*",
        ar: "البريد الالكتروني",
      },
      successLabelMobile: {
        en: "Mobile Number*",
        ar: "رقم الجوال",
      },
      qrTitle: {
        en: "Your Unique Configuration QR",
        ar: "رمز الاستجابة السريعة لتكوينك",
      },
      qrDescription: {
        en: "Show this QR code to your nearest dealer to access your saved configuration.",
        ar: "أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.",
      },
      qrNoteLabel: {
        en: "Note:",
        ar: "ملاحظة:",
      },
      qrNote: {
        en: "Only authorized dealers can view this configuration.",
        ar: "يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.",
      },
      qrDownload: {
        en: "Download QR",
        ar: "تحميل QR",
      },
      qrSave: {
        en: "Save QR",
        ar: "حفظ QR",
      },
      errorTitle: {
        en: "Submission Failed",
        ar: "فشل التقديم",
      },
      errorMessage: {
        en: "We encountered an error, please try again.",
        ar: "واجهنا خطأ، يرجى المحاولة مرة أخرى.",
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: "Exterior Design & Styling",
        ar: "التصميم الخارجي والتصميم",
      },
      interiorComfort: {
        en: "Interior Comfort & Features",
        ar: "الراحة والميزات الداخلية",
      },
      technologyConvenience: {
        en: "Technology & Convenience",
        ar: "التكنولوجيا والراحة",
      },
      safetyAssistance: {
        en: "Safety & Driver Assistance",
        ar: "السلامة ومساعدة السائق",
      },
    },
    sidePanel: {
      menu: {
        en: "Main Menu",
        ar: "القائمة الرئيسية",
      },
      search: {
        en: "Search",
        ar: "بحث",
      },
      settings: {
        en: "Settings",
        ar: "الإعدادات",
      },
      profile: {
        en: "Profile",
        ar: "الملف الشخصي",
      },
      notifications: {
        en: "Notifications",
        ar: "الإشعارات",
      },
      help: {
        en: "Help & Support",
        ar: "المساعدة والدعم",
      },
      filter: {
        en: "Filter Options",
        ar: "خيارات التصفية",
      },
      favorites: {
        en: "Favorites",
        ar: "المفضلة",
      },
      history: {
        en: "History",
        ar: "التاريخ",
      },
      tools: {
        en: "Tools",
        ar: "الأدوات",
      },
      variants: {
        en: "Variants",
        ar: "الأنواع",
      },
      exteriorColor: {
        en: "Exterior Color",
        ar: "لون الهيكل",
      },
      interiorColor: {
        en: "Interior Color",
        ar: "ألوان المقصورة",
      },
      features: {
        en: "Features",
        ar: "الميزات",
      },
      step: {
        en: "Step",
        ar: "خطوة",
      },
      openPanel: {
        en: "Open Panel",
        ar: "فتح اللوحة",
      },
      closePanel: {
        en: "Close Panel",
        ar: "إغلاق اللوحة",
      },
    },
    landscape: {
      message: {
        en: "Please rotate your device to portrait mode",
        ar: "يرجى تدوير جهازك إلى الوضع الرأسي",
      },
      subtitle: {
        en: "For the best experience, please turn your device upright.",
        ar: "للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.",
      },
    },
    cameraViewToggle: {
      ext: {
        en: "Ext",
        ar: "خارجي",
      },
      int: {
        en: "Int",
        ar: "داخلي",
      },
      dualTone: {
        en: "Dual-tone",
        ar: "لون مزدوج",
      },
    },
    bottomDrawer: {
      variants: {
        en: "Variants",
        ar: "الأنواع",
      },
      interactions: {
        en: "Interactions",
        ar: "التفاعلات",
      },
      accessories: {
        en: "Options & Accessories",
        ar: "الإكسسوارات",
      },
      features: {
        en: "Features",
        ar: "الميزات",
      },
      highlights: {
        en: "Highlights",
        ar: "الميزات البارزة",
      },
      moreOptions: {
        en: "More Options",
        ar: "المزيد من الخيارات",
      },
      colorSelector: {
        en: "Color Selector",
        ar: "محدد اللون",
      },
      exteriorColor: {
        en: "Exterior Color",
        ar: "لون الهيكل",
      },
      interiorColor: {
        en: "Interior Color",
        ar: "لون المقصورة",
      },
      wheels: {
        en: "Wheels",
        ar: "العجلات",
      },
      roofs: {
        en: "Roofs",
        ar: "الأسقف",
      },
      performance: {
        en: "Performance",
        ar: "الأداء",
      },
      packages: {
        en: "Packages",
        ar: "الحزم",
      },
      chargingOptions: {
        en: "Charging Options",
        ar: "خيارات الشحن",
      },
      safetyServices: {
        en: "Safety & Services",
        ar: "السلامة والخدمات",
      },
      expand: {
        en: "Expand",
        ar: "توسيع",
      },
      minimize: {
        en: "Minimize",
        ar: "تصغير",
      },
      freeRoamView: {
        en: "Free Roam View",
        ar: "عرض التجوال الحر",
      },
      featureView: {
        en: "Featured View",
        ar: "العرض المميز",
      },
    },
    qr: {
      scan: {
        en: "Scan QR",
        ar: "امسح رمز الاستجابة السريعة",
      },
      scanWithPhone: {
        en: "Scan with your phone to view in your Space",
        ar: "امسح باستخدام هاتفك لعرضه في مساحتك",
      },
    },
    share: {
      title: {
        en: "Share Configuration",
        ar: "مشاركة التكوين",
      },
      description: {
        en: "Share your vehicle configuration with others",
        ar: "شارك تكوين سيارتك مع الآخرين",
      },
      twitterTitle: {
        en: "Check out my GMC Yukon configuration!",
        ar: "شاهد تكوين سيارتي شيفروليه ترافيرس!",
      },
      whatsappTitle: {
        en: "Check out my GMC Yukon configuration!",
        ar: "شاهد تكوين سيارتي شيفروليه ترافيرس!",
      },
      linkedinTitle: {
        en: "My GMC Yukon Configuration",
        ar: "تكويني لسيارة شيفروليه ترافيرس",
      },
      linkedinSummary: {
        en: "Check out my custom GMC Yukon configuration!",
        ar: "شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!",
      },
      emailSubject: {
        en: "Check out my GMC Yukon Configuration",
        ar: "شاهد تكوين سيارتي شيفروليه ترافيرس",
      },
      emailBody: {
        en: "I configured my dream GMC Yukon. Take a look:",
        ar: "قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:",
      },
      linkLabel: {
        en: "Or copy link:",
        ar: "أو انسخ الرابط:",
      },
      copied: {
        en: "Copied",
        ar: "تم نسخ الرابط إلى الحافظة!",
      },
      copy: {
        en: "Copy",
        ar: "نسخ",
      },
      saveAndShare: {
        en: "Save & Share",
        ar: "حفظ ومشاركة",
      },
      copyLink: {
        en: "Copy link",
        ar: "نسخ الرابط",
      },
      facebook: {
        en: "Facebook",
        ar: "Facebook",
      },
      twitter: {
        en: "Twitter",
        ar: "Twitter",
      },
      gmail: {
        en: "Gmail",
        ar: "Gmail",
      },
      whatsapp: {
        en: "Whatsapp",
        ar: "Whatsapp",
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: "Matching Vehicles Found",
        ar: "تم العثور على المركبات المطابقة",
      },
    },
    bottomBar: {
      priceLabel: {
        en: "ex showroom price",
        ar: "سعر صالة العرض",
      },
      leads: {
        en: "Save",
        ar: "حفظ",
      },
      findLocalMatches: {
        en: "Find Local Matches",
        ar: "اعثر على التطابقات المحلية",
      },
      save: {
        en: "Save",
        ar: "حفظ",
      },
      item: {
        en: "Item",
        ar: "العنصر",
      },
      items: {
        en: "Items",
        ar: "عناصر",
      },
      totalToPay: {
        en: "Total to pay",
        ar: "الإجمالي للدفع",
      },
      bookTestDrive: {
        en: "Save",
        ar: "حفظ",
      },
      requestQuote: {
        en: "Request a Quote",
        ar: "طلب عرض سعر",
      },
      downloadBrochure: {
        en: "Download Brochure",
        ar: "تحميل الكتيب",
      },
    },
    priceSummary: {
      title: {
        en: "Price Summary",
        ar: "ملخص السعر",
      },
      enterLocation: {
        en: "Enter Location",
        ar: "أدخل الموقع",
      },
      selectCountry: {
        en: "Location",
        ar: "الموقع",
      },
      selectCity: {
        en: "Location",
        ar: "الموقع",
      },
      note: {
        en: "Note : Prices might vary by state and all prices listed are on road price, T&C apply.",
        ar: "ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.",
      },
      breakdown: {
        en: "Price Breakdown",
        ar: "تفصيل السعر",
      },
      color: {
        en: "Color",
        ar: "اللون",
      },
      variant: {
        en: "Variant",
        ar: "الفئة",
      },
      included: {
        en: "Included",
        ar: "مشمول",
      },
      total: {
        en: "Total",
        ar: "الإجمالي",
      },
    },
    featureDialog: {
      playPause: {
        en: "Play-Pause",
        ar: "تشغيل-إيقاف",
      },
    },
    selectDealership: {
      title: {
        en: "Select a Dealer",
        ar: "اختر وكيلاً",
      },
      subtitle: {
        en: "Please select a dealer to find local matches similar to your configuration",
        ar: "يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك",
      },
      city: {
        en: "UAE",
        ar: "الإمارات",
      },
      dealersIn: {
        en: "Dealers in",
        ar: "وكلاء في",
      },
      dealersInCity: {
        en: "Dealers in {{city}}",
        ar: "وكلاء في {{city}}",
      },
      availableInLocations: {
        en: "Available in {{count}} locations of {{city}}",
        ar: "متوفرة في {{count}} مواقع في {{city}}",
      },
      proceed: {
        en: "Select Dealer and Proceed to Configurator",
        ar: "اختر الوكيل وتابع إلى المتكون",
      },
    },
    countryNames: {
      UAE: {
        en: "UAE",
        ar: "الإمارات",
      },
      KSA: {
        en: "KSA",
        ar: "السعودية",
      },
      KW: {
        en: "Kuwait",
        ar: "الكويت",
      },
      default: {
        en: "UAE",
        ar: "الإمارات",
      },
    },
    localMatches: {
      exterior: {
        en: "Exterior",
        ar: "خارجي",
      },
      interior: {
        en: "Interior",
        ar: "داخلي",
      },
      dualTone: {
        en: "Dual-tone",
        ar: "لون مزدوج",
      },
      yourConfiguration: {
        en: "Current Configuration",
        ar: "التكوين الحالي",
      },
      viewConfiguration: {
        en: "View your configuration details",
        ar: "عرض تفاصيل تكوينك",
      },
      yourBuildDetails: {
        en: "Your Build Details",
        ar: "تفاصيل بنائك",
      },
      options: {
        en: "Options",
        ar: "الخيارات",
      },
      save: {
        en: "Save",
        ar: "حفظ",
      },
      dealersIn: {
        en: "Dealers in",
        ar: "وكلاء في",
      },
      changeDealer: {
        en: "Change Dealer",
        ar: "تغيير الوكيل",
      },
      carsAvailable: {
        en: "Cars Available at the Selected Dealership",
        ar: "السيارات المتوفرة لدى الوكيل المختار",
      },
      matchesFound: {
        en: "Matches found",
        ar: "تطابق تم العثور عليه",
      },
      startingFrom: {
        en: "Starting from",
        ar: "يبدأ من",
      },
      getQuote: {
        en: "Proceed",
        ar: "احصل على عرض سعر",
      },
      viewDifferences: {
        en: "View Differences",
        ar: "عرض الاختلافات",
      },
      match: {
        en: "Match",
        ar: "تطابق",
      },
      closestMatchFound: {
        en: "Closest Match Found",
        ar: "أقرب تطابق تم العثور عليه",
      },
      buildDetails: {
        en: "Build Details",
        ar: "تفاصيل التكوين",
      },
      trim: {
        en: "Trim",
        ar: "الفئة",
      },
      matchScoreTooltip: {
        en: "This score indicates how closely this vehicle matches your configuration.",
        ar: "يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.",
      },
      exactMatch: {
        en: "Exact Match",
        ar: "تطابق تام",
      },
      similarMatch: {
        en: "Close Match",
        ar: "تطابق قريب",
      },
      availableNow: {
        en: "Available Now",
        ar: "متوفر الآن",
      },
      whatYouConfigured: {
        en: "What you configured",
        ar: "ما قمت بتكوينه",
      },
      matchingInStock: {
        en: "Matching vehicle available in stock",
        ar: "مركبة مطابقة متوفرة في المخزون",
      },
      noMatches: {
        en: "No matching vehicles found in your area.",
        ar: "لم يتم العثور على سيارات مطابقة في منطقتك.",
      },
      closeMatchDetails: {
        en: "Close Match Details",
        ar: "تفاصيل التطابق القريب",
      },
      matchesYourSelection: {
        en: "Matches your selection",
        ar: "يطابق اختيارك",
      },
      sameAsWhatYouSelected: {
        en: "Same as what you selected",
        ar: "نفس ما اخترته",
      },
      replacesYourSelection: {
        en: "Replaces your selection",
        ar: "يستبدل اختيارك",
      },
      selectionNotIncluded: {
        en: "Selection not included",
        ar: "الاختيار غير مشمول",
      },
      includedInThisVehicle: {
        en: "Included in this vehicle",
        ar: "مشمول في هذه المركبة",
      },
      additionalFeatureAlreadyIncluded: {
        en: "Additional feature already included",
        ar: "ميزة إضافية مشمولة بالفعل",
      },
      stockDisclaimer: {
        en: "Available stock may include extra features not selected in your original configuration.",
        ar: "قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.",
      },
      additionalInstalledOptionsLegend: {
        en: "Included in this vehicle",
        ar: "مشمول في هذه المركبة",
      },
      vehicleDetails: {
        en: "Vehicle Details",
        ar: "تفاصيل المركبة",
      },
      installedOptionsTitle: {
        en: "Comes with vehicle",
        ar: "يأتي مع المركبة",
      },
      additionalInstalledOptionsSubtitle: {
        en: "Additional Installed Options:",
        ar: "الخيارات المركبة الإضافية:",
      },
      specTrim: {
        en: "Trim",
        ar: "الفئة",
      },
      specExteriorColor: {
        en: "Exterior Color",
        ar: "اللون الخارجي",
      },
      specInteriorColor: {
        en: "Interior Color",
        ar: "اللون الداخلي",
      },
      specWheels: {
        en: "Wheels",
        ar: "العجلات",
      },
      specPackage: {
        en: "Package",
        ar: "الحزمة",
      },
      title: {
        en: "Configurations Found",
        ar: "تكوينات تم العثور عليها",
      },
      message: {
        en: "We Found a few local matches similar to your current configuration. Click to view Matches",
        ar: "وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات",
      },
    },
    trim: {
      selection: {
        en: "Trim Selection",
        ar: "اختيار الفئة",
      },
    },
    paymentPage: {
      exteriorColor: {
        en: "Exterior Color",
        ar: "لون الهيكل",
      },
      interiorColor: {
        en: "Interior Color",
        ar: "لون المقصورة",
      },
      accessory: {
        en: "Accessory",
        ar: "ملحق",
      },
      closePayment: {
        en: "Close",
        ar: "إغلاق",
      },
    },
    packageModal: {
      includes: {
        en: "Includes:",
        ar: "يشمل:",
      },
      of: {
        en: "of",
        ar: "من",
      },
      totalPriceChange: {
        en: "Total Price Change:",
        ar: "إجمالي تغيير السعر:",
      },
      item: {
        en: "Item",
        ar: "غرض",
      },
      items: {
        en: "Items",
        ar: "أغراض",
      },
      addPackage: {
        en: "Add Package",
        ar: "إضافة الحزمة",
      },
      noThanks: {
        en: "No Thanks",
        ar: "لا شكراً",
      },
      alreadyApplied: {
        en: "Already Applied",
        ar: "مُطبّق بالفعل",
      },
    },
    changesRequired: {
      en: "Changes Required",
      ar: "التغييرات المطلوبة",
    },
    changesRequiredDescription: {
      en: "The package you selected conflicts with your current selections. Please review the changes below.",
      ar: "الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.",
    },
    itemsThatWillBeRemoved: {
      en: "Items that will be removed",
      ar: "العناصر التي سيتم إزالتها",
    },
    itemsThatWillBeAdded: {
      en: "Items that will be added",
      ar: "العناصر التي سيتم إضافتها",
    },
    applyChanges: {
      en: "Apply Changes",
      ar: "تطبيق التغييرات",
    },
    previewFeature: {
      en: "Preview Feature",
      ar: "معاينة الميزة",
    },
    exploreFeatures: {
      en: "Explore Features",
      ar: "استكشف الميزات",
    },
    buildAndPrice: {
      en: "Build & Price",
      ar: "البناء والسعر",
    },
    features: {
      preview: {
        en: "Preview Feature",
        ar: "معاينة المميزات",
      },
      playAll: {
        en: "PLAY ALL",
        ar: "تشغيل الكل",
      },
      title: {
        en: "Features",
        ar: "المميزات",
      },
    },
    header: {
      localMatches: {
        en: "Local\nMatches",
        ar: "التطابقات\nالمحلية",
      },
      matchingVehicles: {
        en: "Matching vehicles",
        ar: "مركبات مطابقة",
      },
      configurationsFound: {
        en: "Configurations Found",
        ar: "تكوينات تم العثور عليها",
      },
    },
    configPanel: {
      playAll: {
        en: "Play All",
        ar: "تشغيل الكل",
      },
      pause: {
        en: "Pause",
        ar: "إيقاف مؤقت",
      },
      exteriorColors: {
        en: "Exterior Colors",
        ar: "ألوان الهيكل الخارجي",
      },
      interiorColors: {
        en: "Interior Colors",
        ar: "ألوان الداخلية",
      },
      roofOptions: {
        en: "Roof Options",
        ar: "خيارات السقف",
      },
      interiorOptions: {
        en: "Interior Options",
        ar: "خيارات الداخلية",
      },
      exteriorAccessories: {
        en: "Exterior Accessories",
        ar: "إكسسوارات خارجية",
      },
      noAdditionalOptions: {
        en: "No additional options available for the selected trim.",
        ar: "لا توجد خيارات إضافية متاحة للفئة المختارة.",
      },
      noFilters: {
        en: "No filters available.",
        ar: "لا توجد فلاتر متاحة.",
      },
      noVariantsMatch: {
        en: "No trims match the selected filters.",
        ar: "لا توجد فئات تطابق الفلاتر المختارة.",
      },
      configurationBreakdown: {
        en: "Configuration Breakdown",
        ar: "تفاصيل التكوين",
      },
      yourConfiguration: {
        en: "Your Configuration",
        ar: "تكوينك",
      },
      trim: {
        en: "Trim",
        ar: "الفئة",
      },
      total: {
        en: "Total",
        ar: "الإجمالي",
      },
    },
    conflictDialog: {
      description: {
        en: "In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.",
        ar: "لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.",
      },
    },
    viewport: {
      changeBackground: {
        en: "Change Background",
        ar: "تغيير الخلفية",
      },
      selectLanguage: {
        en: "Switch Language (عربي)",
        ar: "تبديل اللغة (EN)",
      },
      menu: {
        en: "Menu",
        ar: "القائمة",
      },
      exploreFeatures: {
        en: "Explore Features",
        ar: "استكشف الميزات",
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: "UAE",
        value: "UAE",
        dealerId: "122255",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai",
          ar: "الكندي للسيارات - القوز 3، المنطقة الصناعية، شارع الشيخ زايد، دبي",
        },
      },
      {
        id: 1,
        country: "UAE",
        value: "UAE",
        dealerId: "256427",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Double Cola Road, Sharjah",
          ar: "الكندي للسيارات - طريق دبل كولا، الشارقة",
        },
      },
      {
        id: 2,
        country: "UAE",
        value: "UAE",
        dealerId: "256428",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah",
          ar: "الكندي للسيارات - شارع الشيخ محمد بن مطر، الفجيرة",
        },
      },
      {
        id: 3,
        country: "UAE",
        value: "UAE",
        dealerId: "256429",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah",
          ar: "الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة",
        },
      },
      {
        id: 4,
        country: "UAE",
        value: "UAE",
        dealerId: "301597",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai",
          ar: "الكندي للسيارات - شارع الاتحاد - صالة عرض الخبيصي، ديرة، دبي",
        },
      },
      {
        id: 8,
        country: "UAE",
        value: "",
        dealerId: "334821",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi - Mirdiff City Center",
          ar: "الغاندي - مردف سيتي سنتر",
        },
      },
      {
        id: 5,
        country: "UAE",
        value: "UAE",
        dealerId: "323482",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Madinat Zayed, Al Dhafra",
          ar: "بن حمودة للسيارات - مدينة زايد، الظفرة",
        },
      },
      {
        id: 6,
        country: "UAE",
        value: "UAE",
        dealerId: "122552",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Al Ain",
          ar: "بن حمودة للسيارات - العين",
        },
      },
      {
        id: 7,
        country: "UAE",
        value: "UAE",
        dealerId: "122233",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Abu Dhabi Main",
          ar: "بن حمودة للسيارات - أبوظبي الرئيسية",
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: "KSA",
        value: "KSA",
        dealerId: "307261",
        dealerName: "Aljomaih Auto",
        label: {
          en: "Aljomaih Auto - Al Aziziyah Madina Road, Jeddah",
          ar: "الجميح للسيارات - العزيزية طريق المدينة، جدة",
        },
      },
      {
        id: 1,
        country: "KSA",
        value: "KSA",
        dealerId: "164034",
        dealerName: "Aljomaih Auto",
        label: {
          en: "Aljomaih Auto - Hail City King Abdullah Road. Hail",
          ar: "الجميح للسيارات - مدينة حائل طريق الملك عبدالله. حائل",
        },
      },
      {
        id: 2,
        country: "KSA",
        value: "KSA",
        dealerId: "164035",
        dealerName: "Aljomaih Auto",
        label: {
          en: "Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf",
          ar: "الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف",
        },
      },
      {
        id: 3,
        country: "KSA",
        value: "KSA",
        dealerId: "307271",
        dealerName: "Aljomaih Auto",
        label: {
          en: "Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah",
          ar: "الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة",
        },
      },
      {
        id: 4,
        country: "KSA",
        value: "KSA",
        dealerId: "260921",
        dealerName: "Aljomaih Auto",
        label: {
          en: "Aljomaih Auto - Al Jededah Road, Arar",
          ar: "الجميح للسيارات - طريق الجديدة، عرعر",
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: "KW",
        value: "KW",
        dealerId: "295892",
        dealerName: "Mohammed Saleh & Reza Yousuf Behbehani Co",
        label: {
          en: "Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai",
          ar: "شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري",
        },
      },
      {
        id: 1,
        country: "KW",
        value: "KW",
        dealerId: "122225",
        dealerName: "Mohammed Saleh & Reza Yousuf Behbehani Co",
        label: {
          en: "Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai, Abdul Hameed Salem Sons Co, Al Rai",
          ar: "شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري،\\nشركة أبناء عبد الحميد سالم، الري",
        },
      },
      {
        id: 2,
        country: "KW",
        value: "KW",
        dealerId: "260852",
        dealerName: "Mohammed Saleh & Reza Yousuf Behbehani Co",
        label: {
          en: "Mohammed Saleh & Reza Yousuf Behbehani Co., Omar Ibn Al Khatab Street, Sharq",
          ar: "شركة محمد صالح ورضا يوسف بهبهاني، شارع عمر بن الخطاب، شرق",
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: "BHD",
        value: "BHD",
        dealerId: "122215",
        dealerName: "National Motor Company W.L.L",
        label: {
          en: "National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy",
          ar: "الشركة الوطنية للسيارات ذ.م.م، القرية 475 طريق الشيخ جابر الصباح",
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: "IQD",
        value: "IQD",
        dealerId: "295876",
        dealerName: "MAC International FZE",
        label: {
          en: "MAC International FZE, Al-Karada Sadoun Street",
          ar: "شركة ماك الدولية (منطقة حرة)، الكرادة شارع السعدون",
        },
      },
      {
        id: 1,
        country: "IQD",
        value: "IQD",
        dealerId: "295879",
        dealerName: "MAC International FZE",
        label: {
          en: "MAC International FZE, Kurdistan District 100 m Street",
          ar: "شركة ماك الدولية (منطقة حرة)، إقليم كردستان، شارع 100 متر",
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: "QAR",
        value: "QAR",
        dealerId: "122234",
        dealerName: "Mannai Trading Company",
        label: {
          en: "Mannai Trading Company W.L.L., Salwa E Industrial Street",
          ar: "شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي",
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: "OMR",
        value: "OMR",
        dealerId: "295854",
        dealerName: "Moosa",
        label: {
          en: "Moosa Abdul Rahman Hassan & Co. LLC, Al Nahda Main Road",
          ar: "موسى عبد الرحمن حسن وشركاه ش.م.م، شارع النهضة الرئيسي",
        },
      },
      {
        id: 1,
        country: "OMR",
        value: "OMR",
        dealerId: "260877",
        dealerName: "Moosa",
        label: {
          en: "Moosa Abdul Rahman Hassan & Co. LLC, Al Firq Road",
          ar: "موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الفرق",
        },
      },
      {
        id: 2,
        country: "OMR",
        value: "OMR",
        dealerId: "260880",
        dealerName: "Moosa",
        label: {
          en: "Moosa Abdul Rahman Hassan & Co. LLC, New Industrial Road",
          ar: "موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الصناعية الجديدة",
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: "JOD",
        value: "JOD",
        dealerId: "189129",
        dealerName: "Leading Vehicles",
        label: {
          en: "The Leading Vehicles Co. Ltd, Al Rawabi",
          ar: "شركة الآليات الرائدة المحدودة، الروابي",
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: "LBP",
        value: "LBP",
        dealerId: "122227",
        dealerName: "RYMCO",
        label: {
          en: "Rasamny Younis Motor Co. SAL, Michael Zakhour Road",
          ar: "شركة رسامني يونس للسيارات (ش.م.ل)، طريق مايكل زخور",
        },
      },
    ],
    default: [
      {
        id: 0,
        country: "UAE",
        value: "UAE",
        dealerId: "122255",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai",
          ar: "الكندي للسيارات - القوز 3، المنطقة الصناعية، شارع الشيخ زايد، دبي",
        },
      },
      {
        id: 1,
        country: "UAE",
        value: "UAE",
        dealerId: "256427",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Double Cola Road, Sharjah",
          ar: "الكندي للسيارات - طريق دبل كولا، الشارقة",
        },
      },
      {
        id: 2,
        country: "UAE",
        value: "UAE",
        dealerId: "256428",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah",
          ar: "الكندي للسيارات - شارع الشيخ محمد بن مطر، الفجيرة",
        },
      },
      {
        id: 3,
        country: "UAE",
        value: "UAE",
        dealerId: "256429",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah",
          ar: "الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة",
        },
      },
      {
        id: 4,
        country: "UAE",
        value: "UAE",
        dealerId: "301597",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai",
          ar: "الكندي للسيارات - شارع الاتحاد - صالة عرض الخبيصي، ديرة، دبي",
        },
      },
      {
        id: 8,
        country: "UAE",
        value: "",
        dealerId: "334821",
        dealerName: "Al Ghandi Auto",
        label: {
          en: "Al Ghandi - Mirdiff City Center",
          ar: "الغاندي - مردف سيتي سنتر",
        },
      },
      {
        id: 5,
        country: "UAE",
        value: "UAE",
        dealerId: "323482",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Madinat Zayed, Al Dhafra",
          ar: "بن حمودة للسيارات - مدينة زايد، الظفرة",
        },
      },
      {
        id: 6,
        country: "UAE",
        value: "UAE",
        dealerId: "122552",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Al Ain",
          ar: "بن حمودة للسيارات - العين",
        },
      },
      {
        id: 7,
        country: "UAE",
        value: "UAE",
        dealerId: "122233",
        dealerName: "Bin Hamoodah Auto",
        label: {
          en: "Bin Hamoodah Auto - Abu Dhabi Main",
          ar: "بن حمودة للسيارات - أبوظبي الرئيسية",
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: "KSA", currency: "SAR", fx: 3.75 },
      UAE: { label: "UAE", currency: "AED", fx: 3.68 },
      OMR: { label: "Oman", currency: "OMR", fx: 0.39 },
      BHD: { label: "Bahrain", currency: "BHD", fx: 0.38 },
      QAR: { label: "Qatar", currency: "QAR", fx: 3.64 },
      JOD: { label: "Jordan", currency: "JOD", fx: 0.71 },
      KW: { label: "Kuwait", currency: "KWD", fx: 0.31 },
      LBP: { label: "Lebanon", currency: "USD", fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: "Iraq", currency: "USD", fx: 1 }, // priced in USD, like Lebanon
      default: { label: "UAE", currency: "AED", fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: "yukon",
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: "+971",
        leadCode: "AE",
      },
      KSA: {
        code: "+966",
        leadCode: "SA",
      },
      KW: {
        code: "+965",
        leadCode: "KW",
      },
      BHD: {
        code: "+973",
        leadCode: "BH",
      },
      IQD: {
        code: "+964",
        leadCode: "IQ",
      },
      LBP: {
        code: "+961",
        leadCode: "LB",
      },
      OMR: {
        code: "+968",
        leadCode: "OM",
      },
      QAR: {
        code: "+974",
        leadCode: "QA",
      },
      JOD: {
        code: "+962",
        leadCode: "JO",
      },
      default: {
        code: "+971",
        leadCode: "AE",
      },
    },
    model: "Yukon",
    modelName: "Yukon",
    pageTitle: "Yukon Configurator",
    client: "GM",
    brand: "gmc",
    modelId: "GCYUKT1MCM",
    configurationId: "l25zrwex1u93",
    externalAnalytics: {
      provider: "adobe",
      brand: "gmc",
      modelName: "yukon",
      vehicleType: "suv",
    },
    presentation: {
      video: "videos/2368/presentation_video_1.webm",
      loadingImage: "images/2368/sliderImage.webp",
      transitionImage: "images/2368/transition_logo.png",
      headerLogo: "images/2368/transition_logo.png",
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: "",
            ar: "",
          },
          value: {
            en: "355/420 HP ",
            ar: "420/355 حصاناً",
          },
          unit: "",
        },
        {
          index: 1,
          show: true,
          title: {
            en: "Torque",
            ar: "عزم الدوران",
          },
          value: {
            en: "518/624 NM",
            ar: "624/518 نيوتن متر",
          },
          unit: "",
        },
        {
          index: 2,
          show: true,
          title: {
            en: "V8 Engine",
            ar: "V8 محرك",
          },
          value: {
            en: "5.3L/6.2L",
            ar: "5.3 لتر/6.2 لتر",
          },
          unit: "",
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: "firstName",
        type: "text",
        required: true,
        labelKey: "saveDialog.labelFirstName",
        placeholderKey: "saveDialog.placeholderFirstName",
      },
      {
        id: "lastName",
        type: "text",
        required: true,
        labelKey: "saveDialog.labelLastName",
        placeholderKey: "saveDialog.placeholderLastName",
      },
      {
        id: "email",
        type: "email",
        required: true,
        labelKey: "saveDialog.labelEmail",
        placeholderKey: "saveDialog.placeholderEmail",
        visibility: ["email", "test-drive", "callback"],
      },
      {
        id: "phone",
        type: "phone",
        required: true,
        labelKey: "saveDialog.labelMobileNumber",
        placeholderKey: "saveDialog.placeholderMobileNumber",
        visibility: ["whatsapp", "test-drive", "callback"],
      },
      {
        id: "model",
        type: "select",
        required: true,
        labelKey: "saveDialog.labelModel",
        placeholderKey: "saveDialog.labelModel",
        optionsSource: "models",
        visibility: ["test-drive", "callback"],
      },
      {
        id: "dealer",
        type: "select",
        required: true,
        labelKey: "saveDialog.labelDealership",
        placeholderKey: "saveDialog.placeholderSelectDealership",
        optionsSource: "dealers",
        visibility: ["test-drive", "callback", "whatsapp", "email"],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: ["test-drive", "callback", "whatsapp"],
      },
      {
        id: "testDriveDate",
        type: "date",
        required: true,
        labelKey: "saveDialog.labelTestDriveDate",
        placeholderKey: "saveDialog.placeholderTestDriveDate",
        visibility: ["test-drive"],
      },
      {
        id: "testDriveTime",
        type: "time",
        required: true,
        labelKey: "saveDialog.labelTestDriveTime",
        placeholderKey: "saveDialog.placeholderTestDriveTime",
        visibility: ["test-drive"],
      },
      {
        id: "comments",
        type: "textarea",
        required: false,
        labelKey: "saveDialog.labelComments",
        placeholderKey: "saveDialog.placeholderComments",
        visibility: ["test-drive", "callback"],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet:
              "https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet: "https://assets.gm.com/responsive-iframe.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              "https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js",
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet:
              "<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,'script',\n'https://connect.facebook.net/en_US/fbevents.js');\nfbq('init', '2090290248171811');\nfbq('track', 'PageView');\n</script>\n<noscript><img height=\"1\" width=\"1\"\nstyle=\"display:none\"\nsrc=\"https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1\"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ",
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet:
              "https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet: "https://assets.gm.com/responsive-iframe.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              "https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js",
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet:
              "<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,'script',\n'https://connect.facebook.net/en_US/fbevents.js');\nfbq('init', '2090290248171811');\nfbq('track', 'PageView');\n</script>\n<noscript><img height=\"1\" width=\"1\"\nstyle=\"display:none\"\nsrc=\"https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1\"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ",
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet:
              "https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet: "https://assets.gm.com/responsive-iframe.min.js",
            iframe: true,
            standalone: true,
          },
          {
            snippet:
              "https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js",
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet:
              "<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,'script',\n'https://connect.facebook.net/en_US/fbevents.js');\nfbq('init', '2090290248171811');\nfbq('track', 'PageView');\n</script>\n<noscript><img height=\"1\" width=\"1\"\nstyle=\"display:none\"\nsrc=\"https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1\"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ",
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      "--default-font-family": "'Stratum GMC', 'Chevy Sans', sans-serif",
      "--bold-font-family": "'Stratum GMC', 'Chevy Sans Medium', sans-serif",
      "--brand-primary": "#B9081B",
      "--brand-primary-hover": "#970716",
      "--brand-secondary": "#CC0000",
      "--brand-accent": "#B9081B",
      "--brand-accent-hover": "#970716",
      "--brand-accent-light": "#f7e4e9",
      "--brand-surface": "#f3f3f3",
      "--brand-surface-alt": "#f3f4f6",
      "--brand-text": "#000000",
      "--brand-text-secondary": "#666666",
      "--brand-text-tertiary": "#bebebe",
      "--brand-border": "#e6e6e6",
      "--brand-border-selected": "#B9081B",
      "--brand-card-bg": "#ffffff",
      "--brand-toggle-gradient-from": "#B9081B",
      "--brand-toggle-gradient-to": "#CC0000",
      "--brand-toggle-knob-border": "#B9081B",
      "--brand-accent-secondary": "#CC0000",
      "--brand-card-radius": "2px",
      "--brand-card-radius-sm": "2px",
      "--brand-card-radius-lg": "3px",
      "--brand-button-radius": "2px",
      "--brand-button-radius-sm": "2px",
      "--brand-pill-radius": "50px",
      "--brand-cta-radius": "2px",
      "--brand-button-border": "var(--brand-accent)",
      "--brand-cta-arrow-bg": "#B9081B",
      "--brand-cta-arrow-fg": "#ffffff",
      "--brand-cta-shimmer": "#B9081B",
      "--brand-cta-loading-bg": "#B9081B",
    },
  },
} as unknown as UIMetadataComplete;
